import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { X, Film, Loader2, Download, RefreshCw, CheckCircle2, Share2, SlidersHorizontal, Plus, Trash2, EyeOff, CalendarRange, ChevronRight, Play, Pause } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SpotVisit, CampingSpot, AppUser, Vehicle } from '../../types';
import {
  buildTripSpec, buildTimeline, buildTileManifest, frameState, isViableTrip, mediaAt, placeTripMedia, collectVideoClips,
  tileUrlFromTemplate, toWorld, bezierPoint, pointAlongPolyline, kenBurnsTransform, sceneTransition, beatPulse,
  type ReplayTimeline, type FrameState, type WorldPoint, type TileRef, type TripSpec, type StopMedia,
  type KenBurnsTransform, type GeoPoint,
} from '../../services/replayEngine';
import { getSpotImages, auth } from '../../services/firebaseService';
import { jamendoEnabled, searchJamendo, proxiedAudioUrl, type JamendoTrack } from '../../services/jamendoService';
import { getTile } from '../../services/tileCache';
import { formatVisitDateRange, formatTripTitle, filterVisitsByPeriod, lastContiguousPeriod, getReplayVisits } from '../../services/visitService';
import { fetchDrivingRoute } from '../../services/routingService';
import { subscribeToVehicles } from '../../services/vehicleService';
import { getVehicleAvatarSvg } from '../../utils/vehicleAvatars';
import { BitmapLru } from '../../utils/bitmapLru';
import { BRAND } from '../../constants/mapColors';
import { renderMusicBuffer, canEncodeAac, encodeAacIntoMuxer, decodeUserAudio, decodeAudioFromUrl, fitAudioToDuration, mixClipAudioIntoMusic, ALL_MUSIC_STYLES, AAC_CONFIG, type MusicStyle } from '../../services/replayAudio';
import { HOSTED_REPLAY_TRACKS } from '../../constants/replayTracks';
import { extractVideoFrames, extractFramesViaElement, frameAt, probeVideoDuration, type ExtractedVideo } from '../../services/videoFrames';
import { mediaCaptureMs } from '../../utils/mediaCaptureDate';
import { saveUserAudio, listUserAudio, deleteUserAudio, type SavedAudio } from '../../services/savedAudioStore';
import { saveDraft, listDrafts, getDraft, deleteDraft, makeDraftId, type ReplayDraft, type ReplayDraftSummary } from '../../services/replayDraftStore';

// Ferdige musikkspor — HOSTET på Firebase Storage (constants/replayTracks),
// strømmes CORS-rent via contentProxy. Ikke buntet i appen → liten OTA/app +
// spor i full lengde (looper ikke tidlig på lange filmer). Tom liste → skjult.
const BUNDLED_TRACKS: { url: string; title: string }[] = HOSTED_REPLAY_TRACKS
  .map(t => ({ url: proxiedAudioUrl(t.url), title: t.title }));
// Default-musikk = et EKTE spor (eier-funn: syntetisert default hørtes for dårlig
// ut). Faller til syntetisert stil hvis ingen hostede spor finnes.
const DEFAULT_TRACK = BUNDLED_TRACKS.find(t => t.title === 'Happy Background') || BUNDLED_TRACKS[0] || null;

/**
 * Reise-replay-overlay (Fase 1–3, designdok + eng-review).
 *
 *   visits ──buildTripSpec──► basisSpec ──[Komponer-panel: velg stopp,
 *        egne bilder/videoklipp]──► effectiveSpec ──► ReplayTimeline
 *                                                        │
 *   [preload: fliser-blobs + foto + media + sprite]  ──► forhåndsvisning (rAF)
 *                                                        │
 *   Orientering (9:16/1:1/16:9) · Skjul stedsnavn (P4: navn av + zoom ≤ 8)
 *                                                        │
 *                «Lagre som video» ──► WebCodecs→MP4  eller  gifenc→GIF
 *                (fallback: MediaRecorder i sanntid)      │
 *                     «Del» (fersk gestus!) / «Last ned» ◄┘
 *
 * Komponer-modusens medier er KUN lokale (File/objectURL) — ingenting lastes
 * opp; de lever videre i den eksporterte fila. Datasammensetting, aldri
 * videoredigering (eng-review D14).
 */

const ORIENTATIONS = {
  '9:16': { w: 1080, h: 1920 },
  '1:1': { w: 1080, h: 1080 },
  '16:9': { w: 1920, h: 1080 },
} as const;
type Orientation = keyof typeof ORIENTATIONS;

const FPS = 30;
const GIF_FPS = 10;
// Basemap: standard OSM for ALLE land (eier-krav etter live-test: Kartverket
// dekker kun Norge — utenlandsstopp ble svarte). OSM krever kreditering —
// tegnes inn i hver frame (drawFrame). CORS: tile.openstreetmap.org sender ACAO *.
const TILE_TEMPLATE = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const BITMAP_CAP = 96;
const PRIVACY_MAX_ZOOM = 8; // P4: fylkesnivå — flis-innbakte stedsnavn uleselige
const MAX_VIDEO_CLIP_SEC = 6;

type Phase = 'preloading' | 'ready' | 'exporting' | 'done' | 'error';
type ExportFormat = 'mp4' | 'gif';

/**
 * Klippestiler (CapCut-inspirert, eier-ønske): farge-/karttone-preset + hvilken
 * musikkstemning som følger med. Musikken er syntetisert i appen — lisensfri.
 */
// Stil-standardene mapper til KLART ulike stemninger (eier-funn: musikken hørtes
// lik ut fordi calm/warm/arctic alle var myke padder). Nå spenner de energisk →
// chill → eterisk. Kan fortsatt overstyres i musikk-arket.
// Stilpakker = komplett «design» (CapCut autocut-inspirert): fargegrade + musikk +
// beat-tempo + overlegg-familie + overgangs-familie. Overleggene/overgangene er nå
// PER PAKKE (før var de globale → pakkene føltes like). `overlays` tegnes over
// bildet/kartet, under UI-teksten. `transition` velger inngangs-bevegelse.
type OverlayKind = 'grain' | 'lightleak' | 'vignette' | 'particles' | 'frame';
interface ClipStyleDef {
  accent: string;
  tint: string;
  music: MusicStyle;
  bpm: number;                         // beat-synk-tempo (Fase 2), matcher musikkens
  overlays: readonly OverlayKind[];    // Fase 1
  transition: 'punch' | 'whip' | 'driftzoom'; // Fase 1
  flash: boolean;                      // hvit flash på hvert klippkutt
}
const CLIP_STYLES: Record<string, ClipStyleDef> = {
  fricamp:    { accent: BRAND,     tint: 'rgba(12,10,9,0.35)', music: 'roadtrip', bpm: 100, overlays: ['lightleak', 'grain'],           transition: 'whip',      flash: true },
  solnedgang: { accent: '#f59e0b', tint: 'rgba(69,26,3,0.38)', music: 'lofi',     bpm: 82,  overlays: ['grain', 'vignette'],           transition: 'punch',     flash: false },
  nordlys:    { accent: '#22d3ee', tint: 'rgba(8,47,73,0.45)', music: 'arctic',   bpm: 60,  overlays: ['particles', 'vignette'],       transition: 'driftzoom', flash: false },
  retro:      { accent: '#d6b370', tint: 'rgba(59,42,20,0.42)', music: 'lofi',    bpm: 90,  overlays: ['grain', 'frame', 'vignette'],  transition: 'punch',     flash: false },
};
type ClipStyle = keyof typeof CLIP_STYLES;

// ── Stilpakke-overlegg (canvas) ──────────────────────────────────────────────
// Alt er billig (cachet støy-flis + gradienter + få partikler) så vi ikke
// reintroduserer treghet/OOM. Deterministisk på tSec → gyldig for offline-eksport.
let _grainTile: HTMLCanvasElement | null = null;
function grainTile(): HTMLCanvasElement | null {
  if (_grainTile) return _grainTile;
  if (typeof document === 'undefined') return null;
  const size = 128;
  const c = document.createElement('canvas'); c.width = size; c.height = size;
  const g = c.getContext('2d'); if (!g) return null;
  const img = g.createImageData(size, size);
  for (let i = 0; i < img.data.length; i += 4) {
    const v = 90 + Math.floor(Math.random() * 165);
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v; img.data[i + 3] = 255;
  }
  g.putImageData(img, 0, 0);
  _grainTile = c; return c;
}

/** Tegner stilpakkens overlegg over bildet/kartet (under UI-teksten). tSec driver
 *  animasjon; deterministisk (ingen Math.random per frame). */
function drawStyleOverlay(ctx: CanvasRenderingContext2D, cfg: ClipStyleDef, W: number, H: number, tSec: number) {
  for (const kind of cfg.overlays) {
    if (kind === 'vignette') {
      const g = ctx.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.35, W / 2, H / 2, Math.max(W, H) * 0.72);
      g.addColorStop(0, 'rgba(0,0,0,0)');
      g.addColorStop(1, 'rgba(0,0,0,0.42)');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    } else if (kind === 'grain') {
      const tile = grainTile();
      if (tile) {
        ctx.save();
        ctx.globalAlpha = 0.06;
        ctx.globalCompositeOperation = 'overlay';
        // Drift flisa deterministisk så kornet «lever» uten Math.random.
        const ox = -((tSec * 91) % 128), oy = -((tSec * 137) % 128);
        for (let x = ox; x < W; x += 128) for (let y = oy; y < H; y += 128) ctx.drawImage(tile, x, y);
        ctx.restore();
      }
    } else if (kind === 'lightleak') {
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      const drift = Math.sin(tSec * 0.6) * 0.5 + 0.5;
      const g1 = ctx.createRadialGradient(W * (0.85 + 0.05 * drift), H * 0.12, 0, W * 0.85, H * 0.12, W * 0.5);
      g1.addColorStop(0, hexToRgba(cfg.accent, 0.30)); g1.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g1; ctx.fillRect(0, 0, W, H);
      const g2 = ctx.createRadialGradient(W * 0.1, H * (0.9 - 0.05 * drift), 0, W * 0.1, H * 0.9, W * 0.42);
      g2.addColorStop(0, 'rgba(255,180,80,0.18)'); g2.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g2; ctx.fillRect(0, 0, W, H);
      ctx.restore();
    } else if (kind === 'particles') {
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      const N = 26;
      for (let i = 0; i < N; i++) {
        const seed = i * 12.9898;
        const fx = (Math.sin(seed) * 0.5 + 0.5);
        const speed = 0.02 + (i % 5) * 0.012;
        const fy = ((Math.cos(seed) * 0.5 + 0.5) - tSec * speed) % 1;
        const y = (fy < 0 ? fy + 1 : fy) * H;
        const x = fx * W + Math.sin(tSec * 0.5 + seed) * 22;
        const r = 1.5 + (i % 4);
        const a = 0.18 + 0.22 * (Math.sin(tSec * 1.3 + seed) * 0.5 + 0.5);
        ctx.fillStyle = hexToRgba(cfg.accent, a);
        ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
    } else if (kind === 'frame') {
      ctx.save();
      const inset = Math.round(Math.min(W, H) * 0.035);
      ctx.strokeStyle = 'rgba(250,250,249,0.55)';
      ctx.lineWidth = Math.max(3, Math.round(Math.min(W, H) * 0.006));
      roundRect(ctx, inset, inset, W - inset * 2, H - inset * 2, 14); ctx.stroke();
      ctx.restore();
    }
  }
}

// Lesbare pakkenavn (vises under mini-previewen i stil-velgeren).
const STYLE_LABELS: Record<string, string> = {
  fricamp: 'Roadtrip', solnedgang: 'Solnedgang', nordlys: 'Nordlys', retro: 'Retro',
};

// Liten, LOOPENDE forhåndsvisning («gif») av en stilpakke — viser fargegrade +
// overlegg + beat-puls så brukeren ser pakkens uttrykk FØR valg (eier-ønske).
// Lett: bittelite canvas, gjenbruker de samme overlegg-/puls-funksjonene.
const StylePreviewCanvas: React.FC<{ styleKey: ClipStyle }> = ({ styleKey }) => {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    const cfg = CLIP_STYLES[styleKey];
    const W = canvas.width, H = canvas.height;
    let raf = 0; const start = performance.now();
    // KRITISK ytelse: dette er en bitteliten dekor-thumbnail, og det er FIRE av
    // dem synlige samtidig. Uten struping tegnet hver på skjermens full-rate (målt
    // 120 fps × 4 = 480 tegninger/sek) → kokte CPU-en sammen med hoved-previewen →
    // HAKKING (enhets-målt). Vi struper til ~12 fps: rAF planlegges fortsatt, men
    // selve (dyre) gradient+overlegg-tegningen skjer maks hver ~80 ms.
    let lastDraw = -1;
    const draw = (now: number) => {
      raf = requestAnimationFrame(draw);
      if (now - lastDraw < 80) return; // ~12 fps — rikelig for en liten thumbnail
      lastDraw = now;
      const tSec = (performance.now() - start) / 1000;
      const pulse = beatPulse(tSec, cfg.bpm, 0.05);
      ctx.clearRect(0, 0, W, H);
      ctx.save();
      ctx.translate(W / 2, H / 2); ctx.scale(pulse, pulse);
      ctx.translate(-W / 2 + Math.sin(tSec * 0.5) * 4, -H / 2 + Math.cos(tSec * 0.4) * 4);
      const g = ctx.createLinearGradient(0, 0, W, H);
      g.addColorStop(0, cfg.accent); g.addColorStop(0.55, '#3a3330'); g.addColorStop(1, '#0c0a09');
      ctx.fillStyle = g; ctx.fillRect(-6, -6, W + 12, H + 12);
      ctx.fillStyle = cfg.tint; ctx.fillRect(-6, -6, W + 12, H + 12);
      ctx.restore();
      drawStyleOverlay(ctx, cfg, W, H, tSec);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [styleKey]);
  return <canvas ref={ref} width={132} height={176} className="w-full h-full block" />;
};

function hexToRgba(hex: string, alpha: number): string {
  const v = hex.replace('#', '');
  const r = parseInt(v.slice(0, 2), 16), g = parseInt(v.slice(2, 4), 16), b = parseInt(v.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** ms → yyyy-mm-dd (lokal) for <input type=date>. */
function msToDateInput(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

interface StopEdit { excluded?: boolean; media: Array<StopMedia & { file: File }> }

interface ReplayOverlayProps {
  visits: SpotVisit[];        // ALLE besøk — veiviserens datosteg filtrerer selv
  initialFrom?: string;       // yyyy-mm-dd fra ev. aktivt datofilter (valgfritt)
  initialTo?: string;
  spots: CampingSpot[];
  user: AppUser;
  onClose: () => void;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Cover-fit: tegn kilde så den fyller (dw×dh) uten forvrengning. */
function drawCover(ctx: CanvasRenderingContext2D, src: CanvasImageSource, sw: number, sh: number, dw: number, dh: number) {
  if (!sw || !sh) return;
  const scale = Math.max(dw / sw, dh / sh);
  const w = sw * scale, h = sh * scale;
  ctx.drawImage(src, (dw - w) / 2, (dh - h) / 2, w, h);
}

/**
 * Cover-fit med Ken Burns-zoom/pan (kb) + en liten inngangs-«push» (slide).
 * `kb.scale >= 1` gir overscan; pan/slide flytter innenfor det. Stiller ved
 * scale=1 og null-slide reduseres til vanlig drawCover.
 */
function drawCoverKenBurns(
  ctx: CanvasRenderingContext2D, src: CanvasImageSource, sw: number, sh: number, dw: number, dh: number,
  kb: KenBurnsTransform, slideX01 = 0, slideY01 = 0,
) {
  if (!sw || !sh) return;
  const base = Math.max(dw / sw, dh / sh);
  const z = Math.max(1, kb.scale);
  const w = sw * base * z, h = sh * base * z;
  const extraX = Math.max(0, w - dw), extraY = Math.max(0, h - dh);
  const x = (dw - w) / 2 + kb.panX * (extraX / 2) + slideX01 * dw;
  const y = (dh - h) / 2 + kb.panY * (extraY / 2) + slideY01 * dh;
  ctx.drawImage(src, x, y, w, h);
}

/** Stabil heltalls-seed fra en medie-URL → deterministisk Ken Burns/overgang per klipp. */
function mediaSeed(url: string): number {
  let h = 0;
  for (let i = 0; i < url.length; i++) h = (h * 31 + url.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** Tynner en polylinje til maks `max` punkter (behold første+siste) — holder spec-en lett. */
function downsample<T>(pts: T[], max: number): T[] {
  if (pts.length <= max) return pts;
  const out: T[] = [];
  const step = (pts.length - 1) / (max - 1);
  for (let i = 0; i < max; i++) out.push(pts[Math.round(i * step)]);
  return out;
}

/**
 * Henter faktiske vei-ruter (OSRM) for hver etappe stop i → i+1. Feiler en
 * etappe (off-road vill-plass, rate-limit, nett nede) → undefined på den plassen
 * så motoren faller til Bezier. Begrenset samtidighet for å skåne demo-serveren.
 */
async function fetchLegRoutes(spec: TripSpec): Promise<Array<GeoPoint[] | undefined>> {
  const stops = spec.stops;
  const legs = Math.max(0, stops.length - 1);
  const out: Array<GeoPoint[] | undefined> = new Array(legs).fill(undefined);
  let next = 0;
  const worker = async () => {
    while (next < legs) {
      const i = next++;
      const a = stops[i], b = stops[i + 1];
      try {
        // Per-etappe tidsavbrudd (3,5 s): en treg/hengende rute-server skal ALDRI
        // gjøre at previewen tar «altfor lang tid» (eier-funn). Etappen faller da
        // til Bezier — ferdige etapper beholder ekte vei. Bundet totaltid.
        const r = await Promise.race([
          fetchDrivingRoute(
            { latitude: a.latitude, longitude: a.longitude },
            { latitude: b.latitude, longitude: b.longitude },
          ),
          new Promise<never>((_, rej) => setTimeout(() => rej(new Error('route-timeout')), 2500)),
        ]);
        const geo = r.coordinates.map(([lon, lat]) => ({ lat, lon }));
        out[i] = geo.length >= 2 ? downsample(geo, 160) : undefined;
      } catch {
        out[i] = undefined; // Bezier-fallback
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(4, Math.max(1, legs)) }, worker));
  return out;
}

function getInitials(name?: string | null, email?: string | null): string {
  const source = (name && name.trim()) || (email && email.split('@')[0]) || '';
  if (!source) return '?';
  const parts = source.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function svgToImage(svg: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  });
}

const CONTENT_PROXY_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/contentProxy';

/**
 * Laster brukerens profilbilde CORS-rent for canvas som et HTMLImageElement
 * (tegnes direkte med drawImage — IKKE via createImageBitmap, som kan feile
 * stille i Android WebView og gi initialer selv når bildet finnes). Prøver
 * direkte crossOrigin (verter m/ ACAO, f.eks. Google), så via contentProxy
 * (ACAO: *) for Firebase Storage-avatarer (bøtta mangler CORS-config).
 * null → fall til initialer.
 */
/**
 * Laster profilbildet CORS-rent for canvas via fetch → blob → createImageBitmap.
 * VIKTIG: IKKE via et `<img crossOrigin>`-element — det utløser onerror i Android
 * WebView selv når kilden har korrekt ACAO (verifisert på enhet). `fetch()` av
 * contentProxy (ACAO: *) gir bytene, og createImageBitmap(blob) dekoder dem
 * uavhengig av Content-Type. Direkte Storage-URL feiler på CORS (bøtta mangler
 * config) → proxy-fallback. (contentProxy-cap hevet til 32 MB for full-res
 * profilbilder — 12 MB-bilder ga tidligere HTTP 500.)
 */
async function loadAvatarImage(url: string): Promise<ImageBitmap | null> {
  const fromFetch = async (fetchUrl: string): Promise<ImageBitmap> => {
    const res = await fetch(fetchUrl);
    if (!res.ok) throw new Error('h' + res.status);
    return await createImageBitmap(await res.blob());
  };
  try { return await fromFetch(url); } catch { /* prøv proxy */ }
  try { return await fromFetch(`${CONTENT_PROXY_URL}?url=${encodeURIComponent(url)}`); } catch { /* initialer */ }
  return null;
}

const ReplayOverlay: React.FC<ReplayOverlayProps> = ({ visits, initialFrom, initialTo, spots, user, onClose }) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [phase, setPhase] = useState<Phase>('preloading');
  const [progress, setProgress] = useState(0);
  // Play/pause for forhåndsvisningen (eier-ønske). pauseElapsedRef holder tSec
  // der vi frøs, så avspilling gjenopptas nøyaktig der (ikke fra start).
  const [paused, setPaused] = useState(false);
  const pauseElapsedRef = useRef(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [doneMsg, setDoneMsg] = useState('');
  const [retryToken, setRetryToken] = useState(0);

  // Fase 2-valg
  const [orientation, setOrientation] = useState<Orientation>('9:16');
  const [hideNames, setHideNames] = useState(false);
  const [format, setFormat] = useState<ExportFormat>('mp4');
  const [clipStyle, setClipStyle] = useState<ClipStyle>('fricamp');
  // Musikk: stil-komponert (default), brukerens egen lydfil (lokal, lastes
  // aldri opp) eller av. Egen fil beholdes som File — leses først ved eksport.
  const [musicMode, setMusicMode] = useState<'style' | 'custom' | 'off' | 'track'>(DEFAULT_TRACK ? 'track' : 'style');
  // Valgt spor (buntet ferdig-spor ELLER Jamendo-søketreff) når musicMode === 'track'.
  const [selectedTrack, setSelectedTrack] = useState<{ url: string; title: string } | null>(DEFAULT_TRACK);
  // Jamendo-søk (kun når VITE_JAMENDO_CLIENT_ID er satt — ellers skjult).
  const [jamendoQuery, setJamendoQuery] = useState('');
  const [jamendoResults, setJamendoResults] = useState<JamendoTrack[]>([]);
  const [jamendoBusy, setJamendoBusy] = useState(false);
  const [jamendoError, setJamendoError] = useState('');
  // Musikk kan overstyres uavhengig av visuell stil (CapCut-speiling): null =
  // følger stilen. Bunn-arket tilbyr forhåndslytting av stemningene.
  const [musicOverride, setMusicOverride] = useState<MusicStyle | null>(null);
  const [musicSheetOpen, setMusicSheetOpen] = useState(false);
  const previewAudioRef = useRef<AudioContext | null>(null);
  const musicLoopRef = useRef<AudioContext | null>(null);
  // Cache av dekodet musikk-buffer → musikken starter UMIDDELBART (eier-funn:
  // hostet default-spor ble hentet+dekodet ved avspilling → sen start).
  const musicBufferCacheRef = useRef(new Map<string, AudioBuffer>());
  // Cache av rendret kartscene (eksport): stopp-segmenter har STATISK kart over
  // mange frames → tegn kartet én gang og blit det, i stedet for å re-rendre per
  // frame. Stor fart-gevinst ved rolig tempo (mange statiske stopp-frames).
  const mapSceneCacheRef = useRef<{ key: string; canvas: HTMLCanvasElement | null }>({ key: '', canvas: null });
  const [customAudioName, setCustomAudioName] = useState('');
  const customAudioRef = useRef<File | null>(null);
  // Brukerens EGNE lyder, lagret lokalt (IndexedDB) og gjenbrukbare på tvers av
  // filmer/økter (eier-ønske). Objekt-URL-er caches per id for forhåndslytting.
  const [savedAudio, setSavedAudio] = useState<SavedAudio[]>([]);
  const [selectedSavedId, setSelectedSavedId] = useState<string | null>(null);
  const savedAudioUrlsRef = useRef(new Map<string, string>());
  useEffect(() => { void listUserAudio().then(setSavedAudio).catch(() => {}); }, []);
  const savedAudioUrl = (rec: SavedAudio): string => {
    let url = savedAudioUrlsRef.current.get(rec.id);
    if (!url) { url = URL.createObjectURL(rec.blob); savedAudioUrlsRef.current.set(rec.id, url); }
    return url;
  };
  const selectSavedAudio = (rec: SavedAudio) => {
    customAudioRef.current = new File([rec.blob], rec.name, { type: rec.blob.type || 'audio/mpeg' });
    setCustomAudioName(rec.name);
    setSelectedSavedId(rec.id);
    setMusicMode('custom');
  };
  const handlePickCustomAudio = async (f: File) => {
    // Velg umiddelbart (responsivt), lagre lokalt i bakgrunnen for gjenbruk.
    customAudioRef.current = f;
    setCustomAudioName(f.name);
    setMusicMode('custom');
    try {
      const rec = await saveUserAudio(f);
      setSelectedSavedId(rec.id);
      setSavedAudio(await listUserAudio());
    } catch { /* lagring feilet (f.eks. privat modus) — lyden virker likevel denne økten */ }
  };
  const removeSavedAudio = async (rec: SavedAudio) => {
    try { await deleteUserAudio(rec.id); } catch { /* ignorer */ }
    const url = savedAudioUrlsRef.current.get(rec.id);
    if (url) { URL.revokeObjectURL(url); savedAudioUrlsRef.current.delete(rec.id); }
    if (selectedSavedId === rec.id) { customAudioRef.current = null; setCustomAudioName(''); setSelectedSavedId(null); if (musicMode === 'custom') setMusicMode(DEFAULT_TRACK ? 'track' : 'style'); }
    setSavedAudio(await listUserAudio());
  };
  // Mobil-først: alle valg ligger bak «Flere valg» — primærflyten er bare
  // forhåndsvisning + Lagre + Del (eier-krav: telefon er hovedmålgruppen).
  const [showAdvanced, setShowAdvanced] = useState(false);
  const dims = ORIENTATIONS[orientation];
  // Forhåndsvisningen rendres i LAVERE oppløsning enn eksporten (eier-ønske:
  // preview trenger ikke være like skarp → raskere/jevnere). Vises på ~340 px
  // uansett; 0,55× av 1080-logikken (≈594 px) er rikelig på skjerm.
  const PREVIEW_SCALE = 0.55;
  const pw = Math.round(dims.w * PREVIEW_SCALE), ph = Math.round(dims.h * PREVIEW_SCALE);
  const style = CLIP_STYLES[clipStyle];

  // Profil-navn/-bilde: AppUser først, ellers live Firebase-auth (dekker native
  // der AppUser-mappingen kan mangle Google-navn/-bilde), ellers e-postnavn.
  const authUser = auth.currentUser;
  const profileDisplayName = user.displayName?.trim() || authUser?.displayName?.trim() || null;
  const profileEmail = user.email || authUser?.email || null;
  const profilePhotoURL = user.photoURL || authUser?.photoURL || null;
  // Fullt profilnavn (eier-ønske: «Jon Borgen Takle», ikke bare «Jon»).
  const pillFullName = profileDisplayName || profileEmail?.split('@')[0] || 'Fricamper';

  // Fase 3: Komponer-panelet
  const [composeOpen, setComposeOpen] = useState(false);
  const [stopEdits, setStopEdits] = useState<Record<string, StopEdit>>({});
  // Medier lagt til på TUR-nivå (CapCut-flyten): flettes kronologisk mellom
  // stoppene via tidsstempelet (File.lastModified) — placeTripMedia (pure).
  const [tripMedia, setTripMedia] = useState<Array<StopMedia & { file: File; atMs?: number }>>([]);
  // Innlasting av valgte filer (lasteindikator): { total, done } mens ingest pågår, ellers null.
  const [ingest, setIngest] = useState<{ total: number; done: number } | null>(null);
  // Verifisering i panelet: trykk på et stopp → bilder av plassen.
  const [expandedSpotId, setExpandedSpotId] = useState<string | null>(null);
  const [spotImages, setSpotImages] = useState<Record<string, string[] | 'loading'>>({});
  // Tempo (eier-feedback ×2: «altfor kjapt»): sekunder per stopp.
  // Default 'lang' (Rolig): eier har to ganger meldt at filmen er for kjapp —
  // rolig skal være utgangspunktet, ikke et valg man må finne.
  const [tempo, setTempo] = useState<'kort' | 'middels' | 'lang'>('lang');

  // Veiviser (eier-flyt): trykk «Reisefilm» → 1) velg periode → 2) velg egne
  // medier → 3) film. Default-periode = SISTE sammenhengende innsjekk-klynge.
  const [wizardStep, setWizardStep] = useState<'date' | 'media' | 'film'>('date');

  // Utkast (eier-ønske): lagre arbeidet (innstillinger + KOPI av valgte klipp) og
  // gjenoppta senere uten å re-velge fra galleriet. Klipp-bytes kopieres lokalt
  // (se replayDraftStore — content://-referanser overlever ikke mellom økter).
  const [drafts, setDrafts] = useState<ReplayDraftSummary[]>([]);
  const [draftBusy, setDraftBusy] = useState(false);
  const [draftMsg, setDraftMsg] = useState('');
  const refreshDrafts = () => { void listDrafts().then(setDrafts).catch(() => {}); };
  useEffect(() => { refreshDrafts(); }, []);

  // Fase 3: reisefilmen leser checkin_log (ÉN stopp per opphold → atskilte gjenbesøk
  // på samme plass vises separat), med fallback til visits-settet når loggen er tom.
  // Reisetavla/badges bruker fortsatt settet — kun filmens datakilde byttes her.
  // NB: «Komponer selv»-panelet keyer per-stopp-redigeringer på spotId, så to opphold
  // på SAMME plass deler foreløpig Komponer-valg (exclude/media) — kjent Fase 3-kant.
  const [replayVisits, setReplayVisits] = useState<SpotVisit[]>(visits);
  useEffect(() => {
    let alive = true;
    void getReplayVisits(user.uid, visits).then(v => { if (alive) setReplayVisits(v); });
    return () => { alive = false; };
  }, [user.uid, visits]);

  const defaultPeriod = useMemo(() => lastContiguousPeriod(replayVisits), [replayVisits]);
  const [filmFrom, setFilmFrom] = useState(() => initialFrom || (defaultPeriod ? msToDateInput(defaultPeriod.fromMs) : ''));
  const [filmTo, setFilmTo] = useState(() => initialTo || (defaultPeriod ? msToDateInput(defaultPeriod.toMs) : ''));

  // Ytterpunkt for hele reisen (tidligste innsjekk → seneste utsjekk) — brukes av
  // hurtigvalgene «Siste dag / Siste tur / Hele reisen» over datofeltene.
  const tripBounds = useMemo(() => {
    let min = Infinity, max = -Infinity;
    for (const v of replayVisits) {
      const s = v.visitedAt || 0;
      const e = typeof v.checkedOutAt === 'number' && v.checkedOutAt >= s ? v.checkedOutAt : s;
      if (s && s < min) min = s;
      if (e > max) max = e;
    }
    return Number.isFinite(min) && max > 0 ? { min, max } : null;
  }, [replayVisits]);

  const applyPeriodPreset = (preset: 'day' | 'trip' | 'all') => {
    if (preset === 'all' && tripBounds) {
      setFilmFrom(msToDateInput(tripBounds.min));
      setFilmTo(msToDateInput(tripBounds.max));
    } else if (preset === 'trip' && defaultPeriod) {
      setFilmFrom(msToDateInput(defaultPeriod.fromMs));
      setFilmTo(msToDateInput(defaultPeriod.toMs));
    } else if (preset === 'day' && tripBounds) {
      // Siste dag med aktivitet = kalenderdagen til seneste utsjekk/innsjekk.
      const d = msToDateInput(tripBounds.max);
      setFilmFrom(d);
      setFilmTo(d);
    }
  };
  // Hvilket hurtigvalg matcher gjeldende datovalg (for uthevet knapp)?
  const activePreset: 'day' | 'trip' | 'all' | null =
    tripBounds && filmFrom === msToDateInput(tripBounds.max) && filmTo === msToDateInput(tripBounds.max) ? 'day'
    : tripBounds && filmFrom === msToDateInput(tripBounds.min) && filmTo === msToDateInput(tripBounds.max) ? 'all'
    : defaultPeriod && filmFrom === msToDateInput(defaultPeriod.fromMs) && filmTo === msToDateInput(defaultPeriod.toMs) ? 'trip'
    : null;

  // Ressurser — ryddes i unmount-effekten.
  const tileBlobsRef = useRef(new Map<string, Blob>());
  const bitmapsRef = useRef(new BitmapLru<ImageBitmap>(BITMAP_CAP));
  const photosRef = useRef(new Map<string, ImageBitmap>());
  // Foto-media dekodes LAT og holdes i en LRU (maks 10) — så 40–50 bilder aldri
  // holdes dekodet samtidig (eier-funn: kræsjet native). Kildefilene (kompakte
  // JPEG-er) ligger i mediaFilesRef og re-dekodes ved behov via ensureMediaPhoto.
  const mediaBitmapsRef = useRef(new BitmapLru<ImageBitmap>(10)); // objectURL → dekodet bilde (LRU)
  const mediaFilesRef = useRef(new Map<string, File>());           // objectURL → kildefil (lat dekoding)
  const mediaFramesRef = useRef(new Map<string, ExtractedVideo>()); // objectURL → dekodede rammer (WebCodecs el. <video>-seek)
  const mediaAudioRef = useRef(new Map<string, HTMLAudioElement>()); // objectURL → klippets egen lyd (bakgrunn, lavere volum)
  const activeClipAudioRef = useRef<string | null>(null); // hvilket klipp-lyd som spiller nå (unngå re-start hver frame)
  const spriteRef = useRef<HTMLImageElement | null>(null);
  const logoRef = useRef<ImageBitmap | null>(null);
  const avatarRef = useRef<ImageBitmap | null>(null);
  const timelineRef = useRef<ReplayTimeline | null>(null);
  const rafRef = useRef(0);
  const abortedRef = useRef(false);
  const startedAtRef = useRef(0);
  const exportedFileRef = useRef<File | null>(null);

  // Besøk innenfor valgt filmperiode (veiviserens datosteg).
  const filmVisits = useMemo(() => {
    const fromMs = filmFrom ? new Date(`${filmFrom}T00:00:00`).getTime() : undefined;
    const toMs = filmTo ? new Date(`${filmTo}T23:59:59.999`).getTime() : undefined;
    return (fromMs == null && toMs == null) ? replayVisits : filterVisitsByPeriod(replayVisits, fromMs, toMs);
  }, [replayVisits, filmFrom, filmTo]);

  const { spec: baseSpec, skippedNoCoords } = useMemo(() => {
    const photoMap = new Map<string, string>();
    for (const s of spots) {
      const first = s.photoUrls?.[0];
      if (first) photoMap.set(s.id, first);
    }
    return buildTripSpec(filmVisits, photoMap);
  }, [filmVisits, spots]);

  // Komponer-redigeringer lagt på basisspec-en (rene data — motoren er uendret):
  // per-stopp-medier fra stopEdits + tur-medier flettet kronologisk (interludes).
  const effectiveSpec: TripSpec = useMemo(() => {
    const stops = baseSpec.stops
      .filter(s => !stopEdits[s.spotId]?.excluded)
      .map(s => {
        const media = stopEdits[s.spotId]?.media;
        return media?.length ? { ...s, media: media.map(({ file, ...m }) => m) } : s;
      });
    if (!tripMedia.length) return { stops };
    const placed = placeTripMedia(stops, tripMedia.map(({ file, atMs, ...media }) => ({ media, atMs })));
    return { stops: placed.stops, interludes: placed.interludes };
  }, [baseSpec, stopEdits, tripMedia]);

  const routePointsRef = useRef<WorldPoint[]>([]);

  // Auto-tittel («Sommeren 2026») fra reisens tidsspenn — tegnes på åpningen.
  const tripTitle = useMemo(() => {
    const stops = effectiveSpec.stops;
    if (stops.length < 2) return '';
    const first = stops[0].visitedAt;
    const lastStop = stops[stops.length - 1];
    const last = Math.max(lastStop.checkedOutAt ?? lastStop.visitedAt, lastStop.visitedAt);
    const year = new Date(last).getFullYear();
    return formatTripTitle(first, last, t('replay.title.summer', 'Sommeren {{year}}', { year }));
  }, [effectiveSpec, t]);

  const timelineOpts = useMemo(() => {
    // Tempo styrer KART-/foto-pacingen (skalerbar tid). VIDEOKLIPP får sitt eget
    // beskyttede slot i motoren (buildTimeline) og komprimeres ALDRI av tempoet —
    // så et klipp spilles i 1× uansett tempo (eier-funn: «altfor fort»).
    const perStop = tempo === 'kort' ? 4 : tempo === 'lang' ? 10 : 7;
    const cap = tempo === 'kort' ? 90 : tempo === 'lang' ? 260 : 160;
    // Grovt gulv for kart-/foto-delen (ikke-video), så bildeløse stopp ikke
    // krymper til ingenting; video-tiden legges på toppen av motoren.
    const nonVideoStops = effectiveSpec.stops.reduce(
      (a, s) => a + (s.media?.some(m => m.kind === 'video') ? 0 : s.media?.length ? 1.4 : s.photoUrl ? 1 : 0.45), 0);
    const floorSec = Math.min(cap, Math.max(18, nonVideoStops * perStop));
    return {
      stopZoom: hideNames ? PRIVACY_MAX_ZOOM : 11,
      driveZoom: Math.min(6.5, hideNames ? PRIVACY_MAX_ZOOM : 6.5),
      minTotalSec: floorSec,
      maxTotalSec: cap,
      // Per-etappe fit-zoom: rammer inn nettopp den ruten, ingen unødig utzoom.
      viewport: { width: dims.w, height: dims.h },
    };
  }, [hideNames, effectiveSpec, tempo, dims.w, dims.h]);

  // Musikk-buffer for gjeldende valg, cachet → umiddelbar start (forhåndslastes
  // under preload). track: dekodet hostet spor; custom: brukerfil; ellers stil.
  const loadPreviewMusicBuffer = async (): Promise<AudioBuffer | null> => {
    const dur = Math.min(timelineRef.current?.totalSec ?? 30, 40);
    const key = musicMode === 'track' && selectedTrack ? `track:${selectedTrack.url}`
      : musicMode === 'custom' && customAudioRef.current ? `custom:${customAudioName}`
      : `style:${musicOverride ?? style.music}:${Math.round(dur)}`;
    const cached = musicBufferCacheRef.current.get(key);
    if (cached) return cached;
    let buf: AudioBuffer;
    try {
      buf = musicMode === 'track' && selectedTrack ? await decodeAudioFromUrl(selectedTrack.url)
        : musicMode === 'custom' && customAudioRef.current ? await decodeUserAudio(customAudioRef.current)
        : await renderMusicBuffer(musicOverride ?? style.music, dur);
    } catch { return null; }
    musicBufferCacheRef.current.set(key, buf);
    return buf;
  };

  // Generasjons-teller for preloaden (se vakt i effekten under) + cache av
  // hentede vei-ruter (OSRM) så en re-preload IKKE henter dem på nytt over nett.
  const preloadGenRef = useRef(0);
  const legRoutesCacheRef = useRef<{ key: string; routes: Array<GeoPoint[] | undefined> } | null>(null);

  // ── Preload (D2) — kun i film-steget (veiviseren styrer resten) ────────────
  useEffect(() => {
    if (wizardStep !== 'film') return;
    // Generasjons-vakt: preloaden kan re-fyres (f.eks. når klipp-datoer løses opp
    // → effectiveSpec endres). En FORELDET kjøring (eldre gen) skal ikke skrive
    // phase/progress — ellers kappes/dupliseres arbeid og shared refs korrumperes.
    const myGen = ++preloadGenRef.current;
    const stalePreload = () => myGen !== preloadGenRef.current;
    abortedRef.current = false;
    // «Genererer …»-gaten vises KUN ved FØRSTE lasting. Når en re-preload utløses
    // (f.eks. klipp-datoer løses opp → placering endres) beholder vi den spillende
    // previewen og bytter tidslinjen sømløst når den er klar — i stedet for å
    // blinke tilbake til gaten (eier-funn: «bruker for lang tid» / hakkete UX).
    const firstLoad = !timelineRef.current;
    if (firstLoad) { setPhase('preloading'); setProgress(0); }
    (async () => {
      // Timing-instrumentering (eier-funn: preview tar «altfor lang tid») — hvert
      // steg logges til konsollen/logcat så flaskehalsen kan måles på enhet.
      const perf0 = performance.now();
      const lap = (label: string) => { try { console.log(`[reisefilm] ${label}: ${Math.round(performance.now() - perf0)}ms`); } catch { /* noop */ } };
      try {
        if (!isViableTrip(effectiveSpec)) throw new Error(t('visitsTab.replay.needTwo', 'Velg en periode med minst 2 innsjekk med posisjon'));
        if (!window.isSecureContext) throw new Error(t('replay.error.insecure', 'Video-eksport krever sikker tilkobling (HTTPS).'));

        // Start musikk-innlastingen HELT FØRST, parallelt med resten. Default-
        // sporet streames via contentProxy (nett) → tidligere ble den fyrt av på
        // slutten og previewen startet før bufferen var klar → «lyd startet først
        // halvveis» (eier-funn). Nå får den hele preload-tiden → klar ved start.
        void loadPreviewMusicBuffer();

        // Ekte vei-ruter per etappe (OSRM) — hentes online nå, bakes inn i
        // spec-en så selve eksporten forblir offline. Feiler en etappe (off-road
        // vill-plass, nett nede) → undefined → motoren faller til Bezier for den.
        // CACHE på stoppenes koordinater: rutene endres ikke når KLIPP-DATOER løses
        // opp (bare plasseringen endres), så en re-preload gjenbruker dem uten nytt
        // nettkall (eier-funn: «for lang tid» — unngå å hente ruter på nytt).
        const routeKey = effectiveSpec.stops.map(s => `${s.latitude.toFixed(5)},${s.longitude.toFixed(5)}`).join('|');
        let legRoutes: Array<GeoPoint[] | undefined>;
        if (legRoutesCacheRef.current?.key === routeKey) {
          legRoutes = legRoutesCacheRef.current.routes;
        } else {
          legRoutes = await fetchLegRoutes(effectiveSpec);
          if (stalePreload()) return;
          legRoutesCacheRef.current = { key: routeKey, routes: legRoutes };
        }
        const routedSpec: TripSpec = { ...effectiveSpec, legRoutes };

        const manifest = buildTileManifest(routedSpec, { width: dims.w, height: dims.h }, timelineOpts);
        // NB: selve TIDSLINJEN bygges først ETTER at videoklippene er dekodet
        // (lenger ned), så hvert klipps slot kan settes til dets FAKTISKE lengde
        // (durationSec) → klippet spilles helt gjennom og kutter i stedet for å
        // fryse på siste ramme (eier-funn: «stopper videoer fremfor å bare spille»).

        const photoStops = effectiveSpec.stops.filter(s => s.photoUrl);
        const mediaItems = [
          ...effectiveSpec.stops.flatMap(s => stopEdits[s.spotId]?.media ?? []),
          ...tripMedia,
        ];
        // +2 = logo + avatar; +8 = oppvarming av bitmaps for de første rammene
        // (se «warm»-steget nederst) så previewen ALDRI spiller av på et halvtegnet
        // kart — den forblir «Genererer …» til alt er klart (eier-krav).
        const WARM_BUDGET = 8;
        const total = manifest.tiles.length + photoStops.length + mediaItems.length + 2 + WARM_BUDGET;
        let loaded = 0;
        // Video-dekoding rapporterer del-fremdrift (0→1) så innlastings-baren
        // beveger seg JEVNT i stedet for å «henge» på hvert videoklipp (eier-ønske).
        const videoFrac = new Map<string, number>();
        const emitProgress = () => {
          if (abortedRef.current) return;
          let vf = 0; for (const f of videoFrac.values()) vf += f;
          setProgress(Math.min(1, (loaded + vf) / total));
        };
        const bump = () => { loaded++; emitProgress(); };

        const photoMedia = mediaItems.filter(m => m.kind === 'photo');
        const videoMedia = mediaItems.filter(m => m.kind === 'video');

        // ── PARALLELT: video-dekoding ∥ fliser ∥ foto (eier-funn: «altfor lang
        // tid»). Video er CPU (WebCodecs), fliser/foto er nett → de overlapper.
        // Fliser lagres som BLOBS her (ikke bitmaps), så de konkurrerer ikke om
        // bitmap-minnet med videorammene. Tidslinjen bygges så snart video er
        // ferdig (durationSec); fliser/foto ventes på etterpå.
        // Video-VARIGHETER (rask mp4-metadata-probe, INGEN dekoding). Tidslinjen
        // trenger klippenes lengde, men selve rammene dekodes nå ON-DEMAND under
        // avspilling/eksport (ensureVideoFrames). Da holder vi bare klippet som spilles
        // + neste i minnet, og hvert klipp får RIKELIG rammer → jevn video uansett
        // antall klipp (eier-funn: mange klipp → hakking da alt ble dekodet på forhånd
        // med for få rammer hver, ELLER kræsj/heng da vi tok mange rammer × mange klipp).
        const probeDurations = async () => {
          let next = 0;
          const CONC = Math.min(8, videoMedia.length);
          await Promise.all(Array.from({ length: CONC }, async () => {
            while (next < videoMedia.length && !abortedRef.current) {
              const m = videoMedia[next++];
              try {
                const sec = await probeVideoDuration(m.file).catch(() => null);
                if (sec) m.durationSec = Math.min(sec, MAX_VIDEO_CLIP_SEC);
                // Klippets EGEN LYD (bakgrunn) — synket i preview, mikset i eksport.
                if (!mediaAudioRef.current.has(m.url)) {
                  const a = new Audio(m.url);
                  a.preload = 'metadata';
                  a.volume = 0.8;
                  mediaAudioRef.current.set(m.url, a);
                }
              } catch { /* varighet ukjent → fallback i buildTimeline */ }
              videoFrac.set(m.url, 1); emitProgress();
            }
          }));
          lap(`video-varigheter (${videoMedia.length})`);
        };

        const fetchTiles = async () => {
          const queue: TileRef[] = [...manifest.tiles];
          await Promise.all(Array.from({ length: 8 }, async () => {
            while (queue.length && !abortedRef.current) {
              const tile = queue.pop()!;
              // Én mislykket flis skal IKKE velte hele filmen. Hopp over — kosmetisk.
              try {
                const url = tileUrlFromTemplate(TILE_TEMPLATE, tile);
                const res = await getTile(url);
                if (res.ok) tileBlobsRef.current.set(`${tile.z}/${tile.x}/${tile.y}`, await res.blob());
              } catch { /* hopp over flisen */ }
              bump();
            }
          }));
          lap(`fliser (${manifest.tiles.length})`);
        };

        const fetchPhotos = async () => {
          await Promise.all(photoStops.map(async s => {
            try {
              if (!photosRef.current.has(s.spotId)) {
                const res = await fetch(s.photoUrl!);
                if (res.ok) {
                  const bmp = await createImageBitmap(await res.blob(), { resizeWidth: 320, resizeQuality: 'medium' } as ImageBitmapOptions);
                  photosRef.current.set(s.spotId, bmp);
                }
              }
            } catch { /* kort uten polaroid */ }
            bump();
          }));
          // Foto-media dekodes IKKE her lenger (eier-funn: 40–50 bilder → kræsj).
          // De dekodes LAT (ensureMediaPhoto) når de faktisk skal vises, og holdes
          // i en LRU (maks 10). Vi teller dem likevel som «lastet» i fremdriften.
          for (let i = 0; i < photoMedia.length; i++) bump();
          lap(`foto (${photoStops.length}+${photoMedia.length})`);
        };

        // Fyr av alle tre samtidig.
        const videoDone = probeDurations();
        const tilesDone = fetchTiles();
        const photosDone = fetchPhotos();

        // Tidslinjen trenger klippenes EKTE lengde → vent på video FØRST og bygg
        // den, mens fliser/foto fortsatt lastes i bakgrunnen.
        await videoDone;
        const durByUrl = new Map<string, number>();
        for (const m of videoMedia) if (typeof m.durationSec === 'number') durByUrl.set(m.url, m.durationSec);
        const withDur = (media?: StopMedia[]) =>
          media?.map(mm => (mm.kind === 'video' && durByUrl.has(mm.url) ? { ...mm, durationSec: durByUrl.get(mm.url) } : mm));
        const timedSpec: TripSpec = {
          ...routedSpec,
          stops: routedSpec.stops.map(s => (s.media ? { ...s, media: withDur(s.media)! } : s)),
          ...(routedSpec.interludes ? { interludes: routedSpec.interludes.map(inter => ({ ...inter, media: withDur(inter.media)! })) } : {}),
        };
        const tl = buildTimeline(timedSpec, { ...timelineOpts, driveZoom: manifest.driveZoom, stopZoom: manifest.stopZoom });
        timelineRef.current = tl;
        const pts: WorldPoint[] = [];
        for (const seg of tl.segments) {
          if (seg.kind !== 'drive') continue;
          if (seg.routePath && seg.routePath.length >= 2) {
            for (let i = 0; i <= 48; i++) pts.push(pointAlongPolyline(seg.routePath, i / 48).pos);
          } else if (seg.curve) {
            for (let i = 0; i <= 32; i++) pts.push(bezierPoint(seg.curve, i / 32));
          }
        }
        routePointsRef.current = pts;

        await Promise.all([tilesDone, photosDone]);
        // Enkeltfliser tolereres (kosmetisk), MEN INGEN fliser lastet (nett nede)
        // → feil + «Prøv igjen» i stedet for en film med helt blankt kart.
        if (!abortedRef.current && manifest.tiles.length > 0 && tileBlobsRef.current.size === 0) {
          throw new Error(t('replay.error.tiles', 'Kunne ikke laste kartet — sjekk nettforbindelsen.'));
        }

        try {
          if (!logoRef.current) {
            const logoRes = await fetch('/Fricamplogo.png');
            logoRef.current = await createImageBitmap(await logoRes.blob());
          }
        } catch { /* tekst-vannmerke */ }
        bump();

        // Avatar + kjøretøy-sprite hentes over NETT (avatar via contentProxy,
        // kjøretøy fra Firestore) — tidligere BLOKKERTE de previewen med opptil
        // ~3 s + nettlatens (eier-funn: «altfor lang tid»). Nå startes de i
        // BAKGRUNNEN og popper inn i rammene når de er klare (drawFrame tåler
        // null: avatar→initialer, sprite→bilen tegnes ikke før den er lastet).
        if (profilePhotoURL && !avatarRef.current) {
          void loadAvatarImage(profilePhotoURL).then(img => { if (img) avatarRef.current = img; }).catch(() => {});
        }
        bump();

        if (!spriteRef.current) {
          void (async () => {
            const vehicles = await new Promise<Vehicle[]>(resolve => {
              // `let` (ikke `const`) → ingen TDZ hvis subscribeToVehicles kaller
              // callbacken SYNKRONT (da er unsub ennå undefined → no-op, trygt).
              let unsub: (() => void) | undefined;
              const finish = (list: Vehicle[]) => { unsub?.(); resolve(list); };
              unsub = subscribeToVehicles(user.uid, finish);
              setTimeout(() => finish([]), 2500);
            });
            const active = vehicles.filter(v => !v.is_retired);
            const chosen = active.find(v => v.is_default) || active[0];
            const svg = chosen?.ai_avatar_svg
              || getVehicleAvatarSvg(chosen?.category || 'motorhome', chosen?.marker_color || BRAND, 120);
            spriteRef.current = await svgToImage(svg).catch(() => null as any);
          })();
        }

        // (Musikk-bufferen ble startet tidlig, parallelt — se toppen av preload.)

        // ── Oppvarming: dekod flis-bitmaps for de FØRSTE rammene FØR vi går til
        // «ready» ──────────────────────────────────────────────────────────────
        // Fliser lastes over som blobs; drawFrame trenger ImageBitmaps. Uten dette
        // ville avspillingen starte på et halvtegnet kart mens bitmaps dekodes
        // lat i loopen. Vi varmer opp jevnt fordelte prøvetider over hele filmen
        // (kartutsnittet endrer seg) så de første sekundene er komplette straks
        // previewen spiller. Del av fremdriften (WARM_BUDGET) → baren når 100 %
        // først når alt faktisk er klart.
        const warmSamples = 6;
        const warmKeys = new Set<string>();
        for (let i = 0; i < warmSamples; i++) {
          const stW = frameState(tl, (i / Math.max(1, warmSamples - 1)) * Math.min(tl.totalSec, 4));
          for (const key of tileKeysFor(stW)) warmKeys.add(key);
        }
        const warmList = [...warmKeys];
        const warmStep = WARM_BUDGET / Math.max(1, warmList.length);
        for (const key of warmList) {
          if (abortedRef.current) return;
          await ensureTile(key);
          loaded += warmStep; emitProgress();
        }
        loaded = total; emitProgress(); // sikre 100 % ved klar

        if (abortedRef.current || stalePreload()) return;
        lap('KLAR → preview');
        if (firstLoad) {
          setProgress(1);
          setPhase('ready');
          startedAtRef.current = performance.now();
        }
        // Re-preload: tidslinjen (timelineRef) er alt byttet over; den kjørende
        // preview-loopen plukker den opp uten gate-blink eller loop-restart.
      } catch (e: any) {
        if (abortedRef.current || stalePreload()) return;
        timelineRef.current = null; // så «Prøv igjen» viser gaten på nytt
        setErrorMsg(String(e?.message || e));
        setPhase('error');
      }
    })();

    return () => {
      abortedRef.current = true;
      cancelAnimationFrame(rafRef.current);
      bitmapsRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSpec, retryToken, orientation, hideNames, wizardStep]);

  // Full opprydding KUN ved unmount (media/foto gjenbrukes på tvers av preloads).
  useEffect(() => () => {
    abortedRef.current = true;
    cancelAnimationFrame(rafRef.current);
    bitmapsRef.current.clear();
    for (const b of photosRef.current.values()) b.close();
    photosRef.current.clear();
    mediaBitmapsRef.current.clear(); // BitmapLru: lukker alle
    mediaFilesRef.current.clear();
    for (const ev of mediaFramesRef.current.values()) for (const f of ev.frames) f.bitmap.close();
    mediaFramesRef.current.clear();
    for (const a of mediaAudioRef.current.values()) { a.pause(); a.src = ''; }
    mediaAudioRef.current.clear();
    for (const url of savedAudioUrlsRef.current.values()) URL.revokeObjectURL(url);
    savedAudioUrlsRef.current.clear();
    logoRef.current?.close();
    avatarRef.current?.close();
    avatarRef.current = null;
    tileBlobsRef.current.clear();
    const pv = previewAudioRef.current;
    previewAudioRef.current = null;
    if (pv) void pv.close().catch(() => {});
    const ml = musicLoopRef.current;
    musicLoopRef.current = null;
    if (ml) void ml.close().catch(() => {});
    for (const edit of Object.values(stopEditsRef.current)) {
      for (const m of edit.media) URL.revokeObjectURL(m.url);
    }
    for (const m of tripMediaRef.current) URL.revokeObjectURL(m.url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const stopEditsRef = useRef(stopEdits);
  stopEditsRef.current = stopEdits;
  const tripMediaRef = useRef(tripMedia);
  tripMediaRef.current = tripMedia;

  const ensureTile = async (key: string): Promise<ImageBitmap | undefined> => {
    const hit = bitmapsRef.current.get(key);
    if (hit) return hit;
    const blob = tileBlobsRef.current.get(key);
    if (!blob) return undefined;
    const bmp = await createImageBitmap(blob);
    if (abortedRef.current) { bmp.close(); return undefined; }
    bitmapsRef.current.set(key, bmp);
    return bmp;
  };

  // Lat foto-dekoding til LRU-en: dekoder KUN når et bilde faktisk skal vises
  // (preview: gjeldende+neste; eksport: gjeldende frame). Så vi holder aldri mer
  // enn ~10 dekodede bilder samtidig, uansett hvor mange brukeren legger til.
  // Kilden er den kompakte JPEG-en (~1600 px); vi dekoder til 1080 px for tegning.
  const inflightPhoto = new Set<string>();
  const ensureMediaPhoto = async (url: string): Promise<void> => {
    if (mediaBitmapsRef.current.has(url) || inflightPhoto.has(url)) return;
    const file = mediaFilesRef.current.get(url);
    if (!file) return;
    inflightPhoto.add(url);
    try {
      const bmp = await createImageBitmap(file, { resizeWidth: 1080, resizeQuality: 'medium' } as ImageBitmapOptions);
      if (abortedRef.current) { bmp.close(); return; }
      mediaBitmapsRef.current.set(url, bmp);
    } catch { /* hopp over */ } finally { inflightPhoto.delete(url); }
  };
  // Sørg for at fotoet for gjeldende (og valgfritt neste) frame er dekodet.
  const ensurePhotoForFrame = async (st: FrameState): Promise<void> => {
    const m = mediaForFrame(st);
    if (m?.item.kind === 'photo') await ensureMediaPhoto(m.item.url);
  };

  // ── ON-DEMAND video-rammer (jevn video uansett antall klipp) ──────────────
  // Vi dekoder IKKE alle klipp på forhånd (ga hakking pga. for få rammer/klipp,
  // eller kræsj pga. for mange rammer × mange klipp). I stedet dekodes ett klipp
  // om gangen — RIKELIG rammer (~jevn video) — når det skal spilles, og de eldste
  // klippene kastes (LRU). Da holder vi bare ~få klipp i minnet samtidig.
  const VIDEO_CLIP_CAP = 3;       // maks klipp holdt dekodet samtidig (LRU) → bundet minne
  const VIDEO_FRAMES = 90;        // rammer per klipp (~15 fps over 6 s = jevn video)
  const VIDEO_DIM = 400;          // px lengste side (skarpt nok for preview ~594 + eksport ~1024)
  const inflightVideo = new Map<string, Promise<ExtractedVideo | null>>();
  const ensureVideoFrames = (url: string): Promise<ExtractedVideo | null> => {
    const cached = mediaFramesRef.current.get(url);
    if (cached) { // LRU-touch: flytt bakerst
      mediaFramesRef.current.delete(url); mediaFramesRef.current.set(url, cached);
      return Promise.resolve(cached);
    }
    const busy = inflightVideo.get(url);
    if (busy) return busy;
    const file = mediaFilesRef.current.get(url);
    if (!file) return Promise.resolve(null);
    const p = (async (): Promise<ExtractedVideo | null> => {
      const wc = (maxFrames: number, maxDim: number) =>
        extractVideoFrames(file, { clipSec: MAX_VIDEO_CLIP_SEC, maxFrames, maxDim, maxFps: 30 }).catch(() => null);
      let ex = await wc(VIDEO_FRAMES, VIDEO_DIM);
      if (!ex || !ex.frames.length) ex = await wc(Math.min(24, VIDEO_FRAMES), 240); // retry lavere ved minnepress
      if (!ex || !ex.frames.length) {
        ex = await extractFramesViaElement(file, { clipSec: MAX_VIDEO_CLIP_SEC, maxFrames: 24, maxDim: 360, maxFps: 12, rejectBlackFrames: true }).catch(() => null);
      }
      if (abortedRef.current) { if (ex) for (const f of ex.frames) f.bitmap.close(); inflightVideo.delete(url); return null; }
      if (ex && ex.frames.length) {
        mediaFramesRef.current.set(url, ex);
        // Kast eldste klipp til vi er innenfor taket (lukk bitmaps → frigjør minne).
        while (mediaFramesRef.current.size > VIDEO_CLIP_CAP) {
          const oldest = mediaFramesRef.current.keys().next().value as string | undefined;
          if (!oldest || oldest === url) break;
          const old = mediaFramesRef.current.get(oldest);
          if (old) for (const f of old.frames) f.bitmap.close();
          mediaFramesRef.current.delete(oldest);
        }
        try { console.log(`[reisefilm] on-demand klipp: ${ex.frames.length} rammer @${VIDEO_DIM}px (holder ${mediaFramesRef.current.size})`); } catch { /* noop */ }
      } else {
        try { console.warn('[reisefilm] on-demand klipp kunne IKKE dekodes — viser kart'); } catch { /* noop */ }
      }
      inflightVideo.delete(url);
      return ex;
    })();
    inflightVideo.set(url, p);
    return p;
  };
  // Preview: kick i gang dekoding av klippet for denne (og valgfritt neste) frame.
  // Fire-and-forget — tegningen faller til kart til rammene er klare, så «popper» videoen inn.
  const ensureVideoForFrame = (st: FrameState): void => {
    const m = mediaForFrame(st);
    if (m?.item.kind === 'video') void ensureVideoFrames(m.item.url);
  };
  // Eksport (offline): VENT på klippets rammer før vi tegner — ikke sanntid, så vi
  // dekoder ett klipp om gangen (LRU kaster forrige) → jevn video + bundet minne.
  const ensureVideoForFrameAwait = async (st: FrameState): Promise<void> => {
    const m = mediaForFrame(st);
    if (m?.item.kind === 'video') await ensureVideoFrames(m.item.url);
  };

  const tileKeysFor = (st: FrameState): string[] => {
    const n = Math.pow(2, st.anchorZoom);
    const scale = Math.pow(2, st.zoom - st.anchorZoom);
    const span = n * 256 * scale;
    const minX = Math.floor((st.center.x - (dims.w / 2) / span) * n);
    const maxX = Math.floor((st.center.x + (dims.w / 2) / span) * n);
    const minY = Math.max(0, Math.floor((st.center.y - (dims.h / 2) / span) * n));
    const maxY = Math.min(n - 1, Math.floor((st.center.y + (dims.h / 2) / span) * n));
    const keys: string[] = [];
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) keys.push(`${st.anchorZoom}/${((x % n) + n) % n}/${y}`);
    }
    return keys;
  };

  /** Aktivt komponer-medieinnslag for en frame (stopp ELLER mellomspill).
   *  `index`/`count` gir klippets plass i plassens sekvens → kartet crossfades
   *  kun inn på FØRSTE og ut på SISTE klipp (nye innsjekk-avsløringer); klipp
   *  midt i sekvensen kuttes rett over uten å blinke kartet. */
  const mediaForFrame = (st: FrameState) => {
    if (st.interlude?.media.length) {
      const count = st.interlude.media.length;
      const { index, local01 } = mediaAt(count, st.interlude.local01);
      if (index >= 0) return { item: st.interlude.media[index], local01, index, count };
      return null;
    }
    if (st.activeStopIndex === null) return null;
    const stop = timelineRef.current!.spec.stops[st.activeStopIndex];
    const media = stop.media;
    if (!media?.length) return null;
    const { index, local01 } = mediaAt(media.length, st.stopLocalProgress01);
    if (index < 0) return null;
    return { item: media[index], local01, index, count: media.length };
  };

  // ── Frame-tegning (identisk for forhåndsvisning og all eksport) ───────────
  // cacheMap=true (eksport): kartscenen caches per statisk tilstand (stopp) og
  // blittes, i stedet for å re-rendre hver frame → mye raskere lagring.
  const drawFrame = (ctx: CanvasRenderingContext2D, st: FrameState, cacheMap = false) => {
    const tl = timelineRef.current!;
    const W = dims.w, H = dims.h;
    const tSec = st.progress01 * tl.totalSec; // global spilletid → beat-synk + overlegg-animasjon
    ctx.fillStyle = '#0c0a09';
    ctx.fillRect(0, 0, W, H);

    const media = mediaForFrame(st);

    // Selve kart-tegningen (til hvilken som helst context) — tiles + tint + rute
    // + stopp + kjøretøy.
    const drawMapScene = (g: CanvasRenderingContext2D) => {
      const n = Math.pow(2, st.anchorZoom);
      const scale = Math.pow(2, st.zoom - st.anchorZoom);
      const span = n * 256 * scale;
      const px = (w: WorldPoint) => ({
        x: (w.x - st.center.x) * span + W / 2,
        y: (w.y - st.center.y) * span + H / 2,
      });
      const size = 256 * scale;
      for (const key of tileKeysFor(st)) {
        const bmp = bitmapsRef.current.get(key);
        if (!bmp) continue;
        const [, xs, ys] = key.split('/');
        const sx = (Number(xs) / n - st.center.x) * span + W / 2;
        const sy = (Number(ys) / n - st.center.y) * span + H / 2;
        g.drawImage(bmp, sx, sy, size + 0.5, size + 0.5);
      }
      // Fargegrade over kartet — men dempet (globalAlpha 0.5) så OSM sine
      // STEDSNAVN (byer/tettsteder bakt inn i flisene) forblir lesbare (eier-ønske:
      // kartet skal vise stedsnavn). Full tint la et mørkt slør over navnene.
      g.save();
      g.globalAlpha = 0.5;
      g.fillStyle = style.tint;
      g.fillRect(0, 0, W, H);
      g.restore();

      const pts = routePointsRef.current;
      const cut = Math.floor(st.routeProgress01 * Math.max(0, pts.length - 1));
      const drawPath = (list: WorldPoint[], color: string) => {
        if (list.length < 2) return;
        g.beginPath();
        const p0 = px(list[0]);
        g.moveTo(p0.x, p0.y);
        for (let i = 1; i < list.length; i++) { const p = px(list[i]); g.lineTo(p.x, p.y); }
        g.strokeStyle = color;
        g.lineWidth = 8;
        g.setLineDash([22, 22]);
        g.lineCap = 'round';
        g.stroke();
        g.setLineDash([]);
      };
      drawPath(pts.slice(0, cut + 1), style.accent);
      drawPath(pts.slice(cut), 'rgba(120,113,108,0.8)');

      tl.spec.stops.forEach((s, i) => {
        const p = px(toWorld(s.latitude, s.longitude));
        const passed = st.routeProgress01 >= i / Math.max(1, tl.spec.stops.length - 1) - 1e-9;
        g.beginPath(); g.arc(p.x, p.y, 16, 0, Math.PI * 2);
        g.fillStyle = passed ? style.accent : '#57534e'; g.fill();
        g.beginPath(); g.arc(p.x, p.y, 7, 0, Math.PI * 2);
        g.fillStyle = '#0c0a09'; g.fill();
      });

      // Stedsnavn-etiketter ved stoppene (eier-ønske). Grådig plassering med
      // kollisjonssjekk → aldri overlappende etiketter, også ved lav zoom der
      // stoppene ligger tett. Hoppes helt over i personvern-modus («Skjul stedsnavn»).
      if (!hideNames) {
        g.font = '700 22px Inter, sans-serif';
        g.textAlign = 'center'; g.textBaseline = 'alphabetic';
        const drawn: { x0: number; y0: number; x1: number; y1: number }[] = [];
        // Aktivt stopp først → vinner plassen sin ved kollisjon.
        const order = tl.spec.stops
          .map((s, i) => ({ s, i }))
          .sort((a, b) => (a.i === st.activeStopIndex ? -1 : b.i === st.activeStopIndex ? 1 : 0));
        for (const { s, i } of order) {
          const p = px(toWorld(s.latitude, s.longitude));
          if (p.x < -40 || p.x > W + 40 || p.y < 20 || p.y > H + 40) continue; // utenfor lerret
          let label = s.name;
          while (label.length > 4 && g.measureText(label).width > 320) label = `${label.slice(0, -2)}…`;
          const tw = g.measureText(label).width;
          const boxH = 34, padX = 12;
          const cx = Math.min(W - tw / 2 - 16, Math.max(tw / 2 + 16, p.x));
          const boxY = p.y - 24 - boxH; // over prikken
          const rect = { x0: cx - tw / 2 - padX, y0: boxY, x1: cx + tw / 2 + padX, y1: boxY + boxH };
          if (drawn.some(d => rect.x0 < d.x1 && rect.x1 > d.x0 && rect.y0 < d.y1 && rect.y1 > d.y0)) continue;
          drawn.push(rect);
          const active = i === st.activeStopIndex;
          g.fillStyle = active ? 'rgba(28,25,23,0.94)' : 'rgba(28,25,23,0.78)';
          roundRect(g, rect.x0, rect.y0, rect.x1 - rect.x0, boxH, 10); g.fill();
          g.fillStyle = active ? style.accent : '#e7e5e4';
          g.fillText(label, cx, boxY + 23);
        }
        g.textAlign = 'left'; g.textBaseline = 'alphabetic';
      }

      if (st.vehicle.visible && spriteRef.current) {
        const p = px(st.vehicle.pos);
        g.save();
        g.translate(p.x, p.y);
        g.rotate((st.vehicle.bearingDeg * Math.PI) / 180);
        g.drawImage(spriteRef.current, -60, -60, 120, 120);
        g.restore();
      }
    };

    // Kartscene — direkte i preview; cachet+blittet i eksport (statiske stopp).
    let mapShown = false; // OSM-kreditering vises KUN når kartet faktisk er synlig
    const renderMap = () => {
      mapShown = true;
      if (!cacheMap) { drawMapScene(ctx); return; }
      const key = `${st.center.x.toFixed(6)},${st.center.y.toFixed(6)},${st.zoom.toFixed(4)},${st.anchorZoom},`
        + `${st.routeProgress01.toFixed(5)},${st.vehicle.visible ? 1 : 0},${st.vehicle.pos.x.toFixed(6)},`
        + `${st.vehicle.pos.y.toFixed(6)},${st.vehicle.bearingDeg.toFixed(2)},${clipStyle},${st.activeStopIndex},${hideNames ? 1 : 0}`;
      const cache = mapSceneCacheRef.current;
      if (cache.key !== key || !cache.canvas) {
        let oc = cache.canvas;
        if (!oc) oc = document.createElement('canvas');
        if (oc.width !== W || oc.height !== H) { oc.width = W; oc.height = H; }
        // willReadFrequently → CPU-basert (software) raster. Ellers deler denne
        // GPU-backede kart-cachen tekstur med eksport-lerretet nedenfor, som igjen
        // deler GPU med HW-videoenkoderen (MediaCodec) → låser hele render-tråden
        // på Android WebView midt i eksporten («del/last ned virker ikke» = eksport
        // henger). CPU-raster gir litt tregere tegning, men aldri deadlock.
        const octx = oc.getContext('2d', { willReadFrequently: true })!;
        octx.fillStyle = '#0c0a09'; octx.fillRect(0, 0, W, H);
        drawMapScene(octx);
        cache.canvas = oc; cache.key = key;
      }
      ctx.drawImage(cache.canvas, 0, 0);
    };

    if (!media) {
      renderMap();
    } else {
      const seed = mediaSeed(media.item.url);
      // Ingen fade-til-kart ved klipp-kantene (eier-funn: kartet «vistes mellom
      // bilder/videoer»). Media dekker hele skjermen og kuttes rett over i
      // hverandre; kartet/ruten vises KUN under kjøre-segmentene (reisen mellom
      // stopp), ikke mellom klipp på/ved et stopp.
      const tr = sceneTransition(seed, media.local01, 0.14, false, false);
      const photoBmp = media.item.kind === 'photo' ? mediaBitmapsRef.current.get(media.item.url) : undefined;
      const vframes = media.item.kind === 'video' ? mediaFramesRef.current.get(media.item.url) : undefined;
      // 1×-avspilling: map til SANNTID inn i klippet (local01 · skjermtid for
      // klippet), ikke til hele videolengden — ellers spilles videoen «hyperloop»
      // (komprimert til slot-tiden). Fryser på siste ramme hvis sloten er lengre.
      const frameBmp = vframes
        ? frameAt(vframes.frames, media.local01 * (st.activeMediaSlotSec || media.item.durationSec || MAX_VIDEO_CLIP_SEC))
        : null;
      // Video tegnes ALLTID fra dekodede ImageBitmap-rammer (aldri et levende
      // <video> → svart på Android WebView / sent+desynket ellers). Har klippet
      // ingen rammer (ustøttet codec på enheten) faller vi til kartet, aldri svart.
      const drawable = media.item.kind === 'photo' ? !!photoBmp : !!frameBmp;
      // Bakteppe: tegn KARTET når vi crossfader (alpha < 1, ankomst/avreise)
      // ELLER når mediet ikke er klart å tegne — ellers ville en udekodet video
      // bli SVART (eier-funn: videoer vises svart etter forrige oppdatering).
      // Er mediet klart og dekker alt (alpha = 1): solid bakteppe, ingen
      // kart-«flash» mellom bilder.
      if (tr.alpha < 0.999 || !drawable) {
        renderMap();
      } else {
        ctx.fillStyle = '#0c0a09';
        ctx.fillRect(0, 0, W, H);
      }
      ctx.globalAlpha = tr.alpha;
      if (photoBmp) {
        // STILLBILDER: rolig Ken Burns-drift (gjør statiske foto levende). INGEN
        // beat-puls (eier-funn: pulseringen så dårlig ut) og ingen inngangs-«punch».
        const kb = kenBurnsTransform(seed, media.local01);
        drawCoverKenBurns(ctx, photoBmp, photoBmp.width, photoBmp.height, W, H, kb, 0, 0);
      } else if (frameBmp) {
        // VIDEO: bare spill klippet — cover-fit, ingen zoom/puls/bevegelse. Rammene
        // avanserer via frameAt (se over). Eier-funn: video skal «bare spille», ikke
        // pulsere/fryse.
        drawCover(ctx, frameBmp, frameBmp.width, frameBmp.height, W, H);
      }
      ctx.globalAlpha = 1;
      // Hvit flash på klippkuttet (pakker med flash:true, f.eks. Roadtrip).
      if (style.flash) {
        const fa = Math.max(0, 1 - media.local01 / 0.08) * 0.5;
        if (fa > 0.01) { ctx.save(); ctx.globalAlpha = fa; ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H); ctx.restore(); }
      }
      // Slørgradient nederst så kort/vannmerke leser godt.
      const grad = ctx.createLinearGradient(0, H - 420, 0, H);
      grad.addColorStop(0, 'rgba(12,10,9,0)');
      grad.addColorStop(1, 'rgba(12,10,9,0.75)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, H - 420, W, 420);
    }

    // Stilpakke-overlegg (Fase 1): korn/lyslekkasje/vignett/partikler/ramme over
    // bildet+kartet, UNDER UI-teksten (pille/kort/logo holdes knivskarpe).
    drawStyleOverlay(ctx, style, W, H, tSec);

    // Avatar-pille — navn faller alltid til noe (profil-navn → e-postnavn →
    // «Fricamper»), så pilla aldri står tom (eier-funn: navnet manglet).
    const pillName = pillFullName;
    ctx.font = '600 34px Inter, sans-serif';
    const pillW = Math.min(720, 140 + ctx.measureText(pillName).width + 28);
    ctx.fillStyle = 'rgba(12,10,9,0.75)';
    roundRect(ctx, 40, 60, pillW, 88, 44);
    ctx.fill();
    if (avatarRef.current) {
      ctx.save();
      ctx.beginPath(); ctx.arc(88, 104, 34, 0, Math.PI * 2); ctx.clip();
      ctx.translate(54, 70);
      const av = avatarRef.current;
      drawCover(ctx, av, av.width, av.height, 68, 68); // cover-fit: ingen forvrengning av ikke-kvadratiske avatarer
      ctx.restore();
    } else {
      ctx.fillStyle = style.accent;
      ctx.beginPath(); ctx.arc(88, 104, 34, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#0c0a09';
      ctx.font = '900 30px Inter, sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(getInitials(profileDisplayName, profileEmail), 88, 105);
    }
    ctx.fillStyle = '#fafaf9';
    ctx.font = '600 34px Inter, sans-serif';
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    ctx.fillText(pillName, 140, 105);

    // Fremdriftsbar
    ctx.fillStyle = 'rgba(41,37,36,0.9)';
    roundRect(ctx, 40, 28, W - 80, 10, 5); ctx.fill();
    ctx.fillStyle = style.accent;
    roundRect(ctx, 40, 28, Math.max(10, (W - 80) * st.progress01), 10, 5); ctx.fill();

    // Vannmerke: FAST i nedre høyre hjørne (eier-krav: KUN et større
    // Fricamp-logo, ingen «FRICAMP»-navnetrekk). Faller til liten tekst kun
    // hvis logo-bildet ikke lastet (bundlet asset — sjelden). Tegnes FØR stopp-
    // kortet så plass-info + thumbnail alltid ligger FORAN logoen (eier-funn:
    // info havnet bak ikonet).
    ctx.save();
    if (logoRef.current) {
      const lh = 220; // 72 → 86 → 112 → 157 → 220 (+40 % til, eier-ønske)
      const lw = (logoRef.current.width / logoRef.current.height) * lh;
      const lx = W - 32 - lw, ly = H - 28 - lh;
      const radius = Math.min(20, lw / 2, lh / 2);
      // Hele logo-merket 20 % gjennomsiktig (eier-ønske) — mer diskret vannmerke.
      ctx.globalAlpha = 0.8;
      // Myk skygge på en avrundet flate bak logoen (bærer skyggen + gir kontrast
      // mot lyse kartutsnitt). Tegnes før klipp så skyggen ikke klippes bort.
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.45)';
      ctx.shadowBlur = 12;
      ctx.shadowOffsetY = 2;
      ctx.fillStyle = 'rgba(12,10,9,0.5)';
      roundRect(ctx, lx, ly, lw, lh, radius);
      ctx.fill();
      ctx.restore(); // gjenoppretter globalAlpha = 0.8 (satt før skygge-blokka)
      // Logo klippet til avrundede hjørner (eier-ønske); beholder 0.8-alpha.
      roundRect(ctx, lx, ly, lw, lh, radius);
      ctx.clip();
      ctx.drawImage(logoRef.current, lx, ly, lw, lh);
    } else {
      ctx.globalAlpha = 0.85;
      ctx.font = '900 22px Inter, sans-serif';
      ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
      ctx.fillStyle = '#e7e5e4';
      ctx.shadowColor = 'rgba(0,0,0,0.6)';
      ctx.shadowBlur = 6;
      ctx.fillText('FRICAMP', W - 36, H - 44);
    }
    ctx.restore();

    // Kompakt stopp-kort
    if (st.activeStopIndex !== null && st.stopCardOpacity > 0.01) {
      const s = tl.spec.stops[st.activeStopIndex];
      const photo = photosRef.current.get(s.spotId);
      const showPolaroid = !media && photo; // media-scenen ER bildet — dropp polaroid
      const cardH = 150;
      const cardTop = H - 200 - cardH;
      ctx.save();
      ctx.globalAlpha = st.stopCardOpacity;
      // Animert inngang per pakke (Fase 3): whip glir inn fra venstre, øvrige opp.
      const rv = 1 - st.stopCardOpacity;
      ctx.translate(style.transition === 'whip' ? rv * -54 : 0, style.transition === 'whip' ? 0 : rv * 28);
      ctx.fillStyle = 'rgba(28,25,23,0.92)';
      roundRect(ctx, 40, cardTop, W - 80, cardH, 28); ctx.fill();
      ctx.fillStyle = '#fafaf9';
      ctx.font = '800 34px Inter, sans-serif';
      ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
      const displayName = hideNames
        ? t('replay.stopN', 'Stopp {{n}}', { n: st.activeStopIndex + 1 })
        : s.name;
      const maxNameWidth = showPolaroid ? W - 80 - 170 - 70 : W - 80 - 70;
      let name = displayName;
      while (name.length > 4 && ctx.measureText(name).width > maxNameWidth) name = `${name.slice(0, -2)}…`;
      ctx.fillText(name, 72, cardTop + 52);
      ctx.fillStyle = '#a8a29e';
      ctx.font = '500 24px Inter, sans-serif';
      // Sted = kommune + fylke (eier-ønske: vis kommune/fylke når plassen er mål).
      const spot = spotById.get(s.spotId);
      const place = hideNames ? undefined : [spot?.municipality, s.county].filter(Boolean).join(', ');
      const meta = [place, t('replay.stopOf', 'stopp {{n}} av {{total}}', { n: st.activeStopIndex + 1, total: tl.spec.stops.length })]
        .filter(Boolean).join(' · ');
      ctx.fillText(meta, 72, cardTop + 86);
      const dateText = formatVisitDateRange(s.visitedAt, s.checkedOutAt);
      ctx.font = '800 24px Inter, sans-serif';
      const dateWidth = ctx.measureText(dateText).width;
      ctx.fillStyle = hexToRgba(style.accent, 0.16);
      roundRect(ctx, 72, cardTop + 100, dateWidth + 32, 38, 19); ctx.fill();
      ctx.fillStyle = style.accent;
      ctx.fillText(dateText, 88, cardTop + 127);
      if (showPolaroid) {
        ctx.translate(W - 190, cardTop - 24);
        ctx.rotate(0.06);
        ctx.fillStyle = '#fafaf9';
        roundRect(ctx, 0, 0, 116, 140, 6); ctx.fill();
        ctx.drawImage(photo!, 8, 8, 100, 104);
      }
      ctx.restore();
    }

    // Auto-tittel på åpningsstoppet (CapCut-inspirert «Catch The Moment»).
    if (st.activeStopIndex === 0 && st.stopCardOpacity > 0.01 && tripTitle) {
      ctx.save();
      ctx.globalAlpha = st.stopCardOpacity;
      // Tittelen glir opp og inn (Fase 3, animert tekst).
      ctx.translate(0, (1 - st.stopCardOpacity) * 36);
      ctx.font = '800 76px Inter, sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = '#fafaf9';
      ctx.shadowColor = 'rgba(0,0,0,0.7)';
      ctx.shadowBlur = 16;
      ctx.fillText(tripTitle, W / 2, H * 0.26);
      ctx.restore();
    }

    // OSM-kreditering (påkrevd av tile-lisensen) — KUN når kartet er synlig
    // (eier: skal ikke vises oppå bilder/videoer). Nede til venstre, lesbar pille.
    if (mapShown) {
    ctx.save();
    ctx.font = '600 20px Inter, sans-serif';
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    const osmText = '© OpenStreetMap';
    const osmW = ctx.measureText(osmText).width;
    const osmY = H - 44;
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = 'rgba(12,10,9,0.85)';
    roundRect(ctx, 28, osmY - 18, osmW + 24, 36, 10); ctx.fill();
    ctx.globalAlpha = 0.95;
    ctx.fillStyle = '#e7e5e4';
    ctx.fillText(osmText, 40, osmY);
    ctx.restore();
    }

  };

  // Klippets EGEN LYD (bakgrunn) synket til rammene i forhåndsvisningen; musikken
  // fortsetter oppå. clipT = sanntid inn i klippet (samme 1×-mapping som rammene).
  const pauseAllClipAudio = () => {
    for (const a of mediaAudioRef.current.values()) if (!a.paused) a.pause();
    activeClipAudioRef.current = null;
  };
  const syncClipAudio = (media: ReturnType<typeof mediaForFrame>, st: FrameState) => {
    const activeUrl = media?.item.kind === 'video' ? media.item.url : null;
    for (const [url, a] of mediaAudioRef.current) if (url !== activeUrl && !a.paused) a.pause();
    if (!activeUrl || !media) { activeClipAudioRef.current = null; return; }
    const a = mediaAudioRef.current.get(activeUrl);
    if (!a) return;
    const clipT = media.local01 * (st.activeMediaSlotSec || media.item.durationSec || MAX_VIDEO_CLIP_SEC);
    const dur = a.duration || MAX_VIDEO_CLIP_SEC;
    if (clipT >= dur - 0.05) { if (!a.paused) a.pause(); return; } // klippet ferdig → stopp lyd
    if (activeClipAudioRef.current !== activeUrl) {
      // Nytt klipp: start ÉN gang (seek + play). IKKE nullstill hver frame
      // (det ga «spiller starten mange ganger» når lyden ikke var lastet ennå).
      activeClipAudioRef.current = activeUrl;
      try { a.currentTime = clipT; } catch { /* før metadata */ }
      void a.play().catch(() => {});
    } else if (a.paused && a.readyState >= 2) {
      try { a.currentTime = clipT; } catch { /* ignorer */ }
      void a.play().catch(() => {}); // var ikke klar ved start; nå klar
    } else if (!a.paused && Math.abs(a.currentTime - clipT) > 0.6) {
      a.currentTime = clipT; // stor drift → korriger sjelden
    }
  };

  // ── Forhåndsvisning ────────────────────────────────────────────────────────
  // SINGLETON-vakt: hver kjøring av effekten tar et nytt «loop-token». Bare loopen
  // med gjeldende token får kjøre — så selv om preloaden kjører flere ganger
  // (f.eks. når klipp-datoer løses opp) og fyrer av flere ready-overganger, stacker
  // vi ALDRI flere rAF-loops (målt: 5 samtidige loops → 288 «fps» → CPU-kok →
  // hakking). Uten dette kan delt `rafRef` avbryte feil loop og lekke gamle.
  const previewLoopRef = useRef(0);
  useEffect(() => {
    if (phase !== 'ready' && phase !== 'done') return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const myLoop = ++previewLoopRef.current; // ugyldiggjør alle tidligere loops
    let alive = true;
    const stopped = () => !alive || abortedRef.current || myLoop !== previewLoopRef.current;
    // Pauset: frys på tSec der vi stoppet, pause videoer, ingen rAF-loop.
    if (paused) {
      const tl = timelineRef.current;
      if (tl) {
        pauseAllClipAudio();
        const st = frameState(tl, (pauseElapsedRef.current / 1000) % tl.totalSec);
        for (const key of tileKeysFor(st)) void ensureTile(key);
        const redraw = () => { if (!alive) return; ctx.save(); ctx.scale(PREVIEW_SCALE, PREVIEW_SCALE); drawFrame(ctx, st); ctx.restore(); };
        void ensurePhotoForFrame(st).then(redraw);
        const pm = mediaForFrame(st); // on-demand: dekod klippet så det vises (ikke kart) når pauset
        if (pm?.item.kind === 'video') void ensureVideoFrames(pm.item.url).then(redraw);
        redraw();
      }
      return () => { alive = false; };
    }
    // Tak på tegne-raten (~40 fps). Videoen har ~20 fps ekte rammer og kart-panorering
    // er rolig, så 40 fps er rikelig — men å tegne den store 594px-canvasen på skjermens
    // fulle 120 Hz stjeler CPU (sammen med thumbnail-animasjonene) → hakking (enhets-
    // målt). tSec følger fortsatt EKTE tid, så avspillingsfarten er uendret.
    const MIN_FRAME_MS = 24;
    let lastDrawT = -1;
    const loop = (now: number) => {
      if (stopped()) return;
      const tl = timelineRef.current!;
      const elapsed = (performance.now() - startedAtRef.current) / 1000;
      // Stopp etter FØRSTE gjennomspilling (eier-ønske): frys på siste frame.
      if (elapsed >= tl.totalSec) {
        const stEnd = frameState(tl, tl.totalSec);
        for (const key of tileKeysFor(stEnd)) void ensureTile(key);
        ctx.save(); ctx.scale(PREVIEW_SCALE, PREVIEW_SCALE); drawFrame(ctx, stEnd); ctx.restore();
        pauseElapsedRef.current = tl.totalSec * 1000;
        setPaused(true);
        return;
      }
      rafRef.current = requestAnimationFrame(loop);
      if (now - lastDrawT < MIN_FRAME_MS) return; // strup til ~40 fps
      lastDrawT = now;
      const tSec = elapsed;
      pauseElapsedRef.current = tSec * 1000;
      const st = frameState(tl, tSec);
      for (const key of tileKeysFor(st)) void ensureTile(key);
      for (const key of tileKeysFor(frameState(tl, Math.min(tl.totalSec, tSec + 1)))) void ensureTile(key);
      // Lat foto-dekoding: gjeldende + neste sekund (så bildet er klart når sloten
      // kommer). drawFrame faller til kart hvis bildet ikke er ferdig dekodet ennå.
      void ensurePhotoForFrame(st);
      void ensurePhotoForFrame(frameState(tl, Math.min(tl.totalSec, tSec + 1)));
      // On-demand video: dekod klippet som spilles NÅ + forhåndshent det som kommer
      // om ~4 s (rekker å dekode før sloten). LRU kaster klipp vi har passert →
      // bundet minne + jevn video uansett antall klipp. drawFrame faller til kart
      // til rammene er klare, så «popper» videoen inn.
      ensureVideoForFrame(st);
      ensureVideoForFrame(frameState(tl, Math.min(tl.totalSec, tSec + 4)));
      // Video tegnes fra dekodede rammer (drawFrame) — ingen levende <video> å
      // spille av her. Bare klippets egen lyd (bakgrunn) synkes under musikken.
      const media = mediaForFrame(st);
      syncClipAudio(media, st);
      ctx.save(); ctx.scale(PREVIEW_SCALE, PREVIEW_SCALE); drawFrame(ctx, st); ctx.restore();
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => { alive = false; cancelAnimationFrame(rafRef.current); pauseAllClipAudio(); };
    // clipStyle i deps: loopen fanger drawFrame-closuren ved oppstart — uten
    // restart ville stilbytte aldri nå canvasen (observert i QA). paused i deps:
    // bytter mellom frys og rAF-loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, orientation, clipStyle, paused]);

  // ── Musikk i forhåndsvisningen (eier-funn fra live-test) ──────────────────
  // Looper valgt spor mens previewen spiller; pauses under musikk-arket
  // (▶-prøvene skal høres alene) og under eksport. AudioContext er tillatt
  // fordi overlayet alltid åpnes av et brukertrykk.
  const stopMusicLoop = () => {
    const c = musicLoopRef.current;
    musicLoopRef.current = null;
    if (c) void c.close().catch(() => {});
  };
  useEffect(() => {
    const active = (phase === 'ready' || phase === 'done') && !musicSheetOpen
      && format === 'mp4' && musicMode !== 'off' && !paused;
    if (!active) { stopMusicLoop(); return; }
    let cancelled = false;
    (async () => {
      stopMusicLoop();
      const ctx = new AudioContext();
      musicLoopRef.current = ctx;
      try { await ctx.resume(); } catch { /* autoplay-vakt — spiller når mulig */ }
      const buf = await loadPreviewMusicBuffer(); // cachet (forhåndslastet) → umiddelbar start
      if (!buf || cancelled || musicLoopRef.current !== ctx) { void ctx.close().catch(() => {}); return; }
      const node = ctx.createBufferSource();
      node.buffer = buf;
      node.loop = true;
      const g = ctx.createGain();
      g.gain.value = 0.9;
      node.connect(g).connect(ctx.destination);
      node.start();
      // iOS/mobil: autoplay-policy kan holde konteksten suspendert til en
      // brukergest. Gjenoppta ved neste trykk hvor som helst (én gang).
      if (ctx.state === 'suspended') {
        const resume = () => { void ctx.resume().catch(() => {}); document.removeEventListener('pointerdown', resume); };
        document.addEventListener('pointerdown', resume, { once: true });
      }
    })();
    return () => { cancelled = true; stopMusicLoop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, musicSheetOpen, format, musicMode, musicOverride, clipStyle, customAudioName, paused, selectedTrack]);

  // ── Eksport ────────────────────────────────────────────────────────────────
  const exportVideo = async () => {
    if (phase === 'exporting') return;
    setPaused(false); // eksport bruker egen deterministisk løkke — frys av
    pauseAllClipAudio(); // klipp-lyd mikses inn i eksport-lyden, ikke via elementene
    setPhase('exporting');
    setProgress(0);
    cancelAnimationFrame(rafRef.current);
    try {
      const tl = timelineRef.current!;
      let blob: Blob;
      let ext = 'mp4';
      if (format === 'gif') {
        blob = await exportViaGif(tl);
        ext = 'gif';
      } else if (typeof VideoEncoder !== 'undefined') {
        blob = await exportViaWebCodecs(tl);
      } else if (typeof MediaRecorder !== 'undefined') {
        blob = await exportViaMediaRecorder(tl);
      } else {
        throw new Error(t('replay.error.unsupported', 'Nettleseren støtter ikke video-eksport. Prøv å oppdatere den.'));
      }
      if (abortedRef.current) return;

      const file = new File([blob], `fricamp-reisefilm-${new Date().toISOString().slice(0, 10)}.${ext}`, { type: blob.type || (ext === 'gif' ? 'image/gif' : 'video/mp4') });
      exportedFileRef.current = file;
      // På native har vi alltid Del/Last ned (Capacitor Share) → vis knappene i stedet
      // for å auto-laste ned. På web auto-lastes ned hvis Web Share ikke støttes.
      if (!Capacitor.isNativePlatform() && !navigator.canShare?.({ files: [file] })) {
        void downloadBlob(file);
        setDoneMsg(t('replay.downloaded', 'Lastet ned'));
      } else {
        setDoneMsg(t('replay.ready', 'Video klar!'));
      }
      setPhase('done');
      startedAtRef.current = performance.now();
    } catch (e: any) {
      if (abortedRef.current) return;
      setErrorMsg(String(e?.message || e));
      setPhase('error');
    }
  };

  const exportViaWebCodecs = async (tl: ReplayTimeline): Promise<Blob> => {
    mapSceneCacheRef.current.key = ''; // fersk kart-cache per eksport
    const { Muxer, ArrayBufferTarget } = await import('mp4-muxer');
    // Eksport-oppløsning: korteste side ~1024 px (eier-ønske «1024p»). Farten
    // kommer fra kart-cachingen (statiske stopp-frames), ikke fra lav oppløsning.
    // Tegner 1080-LOGIKKEN skalert (layout bevares).
    const exportScale = Math.min(1, 1024 / Math.min(dims.w, dims.h));
    const ew = Math.round((dims.w * exportScale) / 2) * 2;
    const eh = Math.round((dims.h * exportScale) / 2) * 2;
    const exportBitrate = Math.max(4_000_000, Math.round(8_000_000 * (ew * eh) / (dims.w * dims.h)));
    let codec = '';
    for (const c of ['avc1.640028', 'avc1.4D0028', 'avc1.42E028']) {
      if ((await VideoEncoder.isConfigSupported({ codec: c, width: ew, height: eh, framerate: FPS })).supported) { codec = c; break; }
    }
    if (!codec) throw new Error(t('replay.error.unsupported', 'Nettleseren støtter ikke video-eksport. Prøv å oppdatere den.'));

    // Overleggsmusikk (stil-komponert eller brukerens egen fil): kun når
    // AAC-encoding støttes — ellers stille video, aldri eksportfeil pga. lyd.
    const withMusic = musicMode !== 'off' && await canEncodeAac();
    const muxer = new Muxer({
      target: new ArrayBufferTarget(),
      video: { codec: 'avc', width: ew, height: eh },
      ...(withMusic ? { audio: { codec: 'aac', sampleRate: AAC_CONFIG.sampleRate, numberOfChannels: AAC_CONFIG.numberOfChannels } } : {}),
      fastStart: 'in-memory',
    });
    let encodeError: unknown = null;
    const encoder = new VideoEncoder({
      output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
      error: e => { encodeError = e; },
    });
    encoder.configure({ codec, width: ew, height: eh, framerate: FPS, bitrate: exportBitrate });

    const off = document.createElement('canvas');
    off.width = ew; off.height = eh;
    // willReadFrequently → software-lerret. `new VideoFrame(off)` blir da en ren
    // CPU-kopi i stedet for en GPU-tekstur-overlevering til HW-enkoderen. Sistnevnte
    // låser render-tråden på Android WebView (verifisert on-device: eksporten henger
    // på et vilkårlig punkt, ingen CDP/Debugger-respons → native deadlock GPU↔codec).
    const ctx = off.getContext('2d', { willReadFrequently: true })!;
    const sx = ew / dims.w, sy = eh / dims.h; // nedskalering av 1080-tegningen
    const totalFrames = Math.ceil(tl.totalSec * FPS);

    for (let i = 0; i < totalFrames; i++) {
      if (abortedRef.current) { encoder.close(); throw new Error('avbrutt'); }
      if (encodeError) throw encodeError;
      const st = frameState(tl, i / FPS);
      await Promise.all(tileKeysFor(st).map(ensureTile));
      await ensurePhotoForFrame(st);
      await ensureVideoForFrameAwait(st);
      ctx.save(); ctx.scale(sx, sy); drawFrame(ctx, st, true); ctx.restore();
      const vf = new VideoFrame(off, { timestamp: (i * 1e6) / FPS, duration: 1e6 / FPS });
      encoder.encode(vf, { keyFrame: i % (FPS * 2) === 0 });
      vf.close();
      // Vent til enkoderen har fordøyd køen. Vaktbikkje: synker ikke køen på 20 s
      // har HW-enkoderen stanset → kast tydelig feil i stedet for å spinne i det
      // uendelige (baren «henger» på X %, og del/last-ned dukker aldri opp).
      let stallStart = performance.now();
      let lastQ = encoder.encodeQueueSize;
      while (encoder.encodeQueueSize > 6) {
        if (encodeError) throw encodeError;
        if (abortedRef.current) { encoder.close(); throw new Error('avbrutt'); }
        await new Promise(r => setTimeout(r, 8));
        const q = encoder.encodeQueueSize;
        if (q < lastQ) { lastQ = q; stallStart = performance.now(); }
        else if (performance.now() - stallStart > 20000) {
          throw new Error(t('replay.error.encoderStall', 'Videoenkoderen svarte ikke. Prøv en kortere film eller GIF.'));
        }
      }
      // Reserver de siste ~10 % til lyd-miksing/finalisering så baren ikke «henger»
      // på 100 % mens lyden legges på (kan ta et par sekunder på lange filmer).
      if (i % 24 === 0) { setProgress((i / totalFrames) * (withMusic ? 0.9 : 1)); await new Promise(r => setTimeout(r)); }
    }
    await encoder.flush();
    if (withMusic) setProgress(0.92);

    if (withMusic && !abortedRef.current) {
      let music: AudioBuffer;
      if (musicMode === 'track' && selectedTrack) {
        try {
          music = await fitAudioToDuration(await decodeAudioFromUrl(selectedTrack.url), tl.totalSec);
        } catch {
          throw new Error(t('replay.error.audioFile', 'Kunne ikke lese lydfilen — prøv en annen fil (MP3/M4A/WAV).'));
        }
      } else if (musicMode === 'custom' && customAudioRef.current) {
        try {
          music = await fitAudioToDuration(await decodeUserAudio(customAudioRef.current), tl.totalSec);
        } catch {
          throw new Error(t('replay.error.audioFile', 'Kunne ikke lese lydfilen — prøv en annen fil (MP3/M4A/WAV).'));
        }
      } else {
        music = await renderMusicBuffer(musicOverride ?? style.music, tl.totalSec);
      }
      // Miks inn videoklippenes egen lyd (lavere) UNDER musikken.
      const clips = collectVideoClips(tl);
      if (clips.length) {
        try { music = await mixClipAudioIntoMusic(music, clips, tl.totalSec); } catch { /* behold ren musikk */ }
      }
      await encodeAacIntoMuxer(muxer, music);
    }

    setProgress(0.99);
    muxer.finalize();
    return new Blob([muxer.target.buffer], { type: 'video/mp4' });
  };

  /** GIF: samme deterministiske frames, lavere fps/oppløsning (størrelse). */
  const exportViaGif = async (tl: ReplayTimeline): Promise<Blob> => {
    mapSceneCacheRef.current.key = ''; // fersk kart-cache per eksport
    const { GIFEncoder, quantize, applyPalette } = await import('gifenc');
    const scale = 360 / Math.min(dims.w, dims.h);
    const gw = Math.round(dims.w * scale / 2) * 2;
    const gh = Math.round(dims.h * scale / 2) * 2;
    const off = document.createElement('canvas');
    off.width = gw; off.height = gh;
    const ctx = off.getContext('2d', { willReadFrequently: true })!;
    const gif = GIFEncoder();
    const totalFrames = Math.ceil(tl.totalSec * GIF_FPS);
    for (let i = 0; i < totalFrames; i++) {
      if (abortedRef.current) throw new Error('avbrutt');
      const st = frameState(tl, i / GIF_FPS);
      await Promise.all(tileKeysFor(st).map(ensureTile));
      await ensurePhotoForFrame(st);
      await ensureVideoForFrameAwait(st);
      ctx.save();
      ctx.scale(scale, scale);
      drawFrame(ctx, st, true);
      ctx.restore();
      const { data } = ctx.getImageData(0, 0, gw, gh);
      const palette = quantize(data, 256);
      const index = applyPalette(data, palette);
      gif.writeFrame(index, gw, gh, { palette, delay: Math.round(1000 / GIF_FPS) });
      if (i % 5 === 0) { setProgress(i / totalFrames); await new Promise(r => setTimeout(r)); }
    }
    gif.finish();
    return new Blob([gif.bytes()], { type: 'image/gif' });
  };

  const exportViaMediaRecorder = (tl: ReplayTimeline): Promise<Blob> =>
    new Promise((resolve, reject) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) { reject(new Error('canvas mangler')); return; }
      const mime = MediaRecorder.isTypeSupported('video/mp4') ? 'video/mp4' : 'video/webm';
      const recorder = new MediaRecorder(canvas.captureStream(FPS), { mimeType: mime, videoBitsPerSecond: 8_000_000 });
      const chunks: Blob[] = [];
      recorder.ondataavailable = e => { if (e.data.size) chunks.push(e.data); };
      recorder.onerror = () => reject(new Error('MediaRecorder-feil'));
      recorder.onstop = () => resolve(new Blob(chunks, { type: mime }));
      recorder.start(500);
      const t0 = performance.now();
      const loop = async () => {
        if (abortedRef.current) { recorder.stop(); return; }
        const tSec = (performance.now() - t0) / 1000;
        if (tSec >= tl.totalSec) { recorder.stop(); return; }
        const st = frameState(tl, tSec);
        await Promise.all(tileKeysFor(st).map(ensureTile));
        await ensurePhotoForFrame(st);
      await ensureVideoForFrameAwait(st);
        ctx.save(); ctx.scale(PREVIEW_SCALE, PREVIEW_SCALE); drawFrame(ctx, st, true); ctx.restore(); // fallback: preview-canvas-oppløsning
        setProgress(tSec / tl.totalSec);
        requestAnimationFrame(loop);
      };
      requestAnimationFrame(loop);
    });

  /** Forhåndslytt (musikk-arket): 6 s av stemningen. Stoppes ved nytt valg/lukking. */
  const stopMusicPreview = () => {
    const c = previewAudioRef.current;
    previewAudioRef.current = null;
    if (c) void c.close().catch(() => {});
  };
  const previewMood = async (mood: MusicStyle) => {
    stopMusicPreview();
    const ctx = new AudioContext();
    previewAudioRef.current = ctx;
    const buf = await renderMusicBuffer(mood, 6);
    if (previewAudioRef.current !== ctx) { void ctx.close().catch(() => {}); return; }
    const node = ctx.createBufferSource();
    node.buffer = buf;
    node.connect(ctx.destination);
    node.onended = () => { if (previewAudioRef.current === ctx) stopMusicPreview(); };
    node.start();
  };
  /** Forhåndslytt av et ferdig spor (6 s fra start). */
  const previewTrack = async (url: string) => {
    stopMusicPreview();
    const ctx = new AudioContext();
    previewAudioRef.current = ctx;
    let buf: AudioBuffer;
    try { buf = await decodeAudioFromUrl(url); } catch { stopMusicPreview(); return; }
    if (previewAudioRef.current !== ctx) { void ctx.close().catch(() => {}); return; }
    const node = ctx.createBufferSource();
    node.buffer = buf;
    const g = ctx.createGain();
    g.gain.value = 0.9;
    node.connect(g).connect(ctx.destination);
    node.onended = () => { if (previewAudioRef.current === ctx) stopMusicPreview(); };
    node.start(0, 0, 6);
  };
  const runJamendoSearch = async () => {
    if (!jamendoQuery.trim() || jamendoBusy) return;
    setJamendoBusy(true); setJamendoError('');
    try {
      setJamendoResults(await searchJamendo(jamendoQuery));
    } catch (e: any) {
      setJamendoError(String(e?.message || e)); setJamendoResults([]);
    } finally {
      setJamendoBusy(false);
    }
  };

  const shareExported = async () => {
    const file = exportedFileRef.current;
    if (!file) { setDoneMsg(t('replay.noFile', 'Ingen video å dele — lagre videoen først.')); return; }
    if (Capacitor.isNativePlatform()) {
      // Native: KUN Capacitor-veien (saveFileNative). Ved feil setter den en synlig
      // melding selv — ikke fall videre til navigator.share (som er upålitelig i
      // Android WebView og ville skjult feilen → «knappen virker ikke»).
      if (await saveFileNative(file)) setDoneMsg(t('replay.shared', 'Delt!'));
      return;
    }
    try {
      await navigator.share({ files: [file], title: 'Fricamp' });
      setDoneMsg(t('replay.shared', 'Delt!'));
    } catch (e: any) {
      if (e?.name !== 'AbortError') void downloadBlob(file);
    }
  };

  // Play/pause: ved gjenopptak rebases startedAtRef så avspillingen fortsetter
  // fra frosset tSec (ikke fra start).
  const togglePause = () => {
    setPaused(p => {
      const next = !p;
      if (!next) {
        const tl = timelineRef.current;
        // Står vi på slutten (ferdig avspilt)? Start forfra ved play.
        if (tl && pauseElapsedRef.current >= tl.totalSec * 1000 - 20) pauseElapsedRef.current = 0;
        startedAtRef.current = performance.now() - pauseElapsedRef.current;
      }
      return next;
    });
  };

  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(String(r.result).split(',')[1] || '');
      r.onerror = () => reject(r.error);
      r.readAsDataURL(blob);
    });

  // Lagre/del en generert fil på native. `navigator.share` for FILER er upålitelig i
  // Android WebView (canShare({files}) gir ofte false → forrige fiks falt tilbake til
  // anker-nedlasting som WebView blokkerer → «Last ned virket fortsatt ikke»). Ekte
  // fiks: skriv fila til app-cachen med Capacitor Filesystem og åpne delings-arket via
  // Share-plugin (der ligger «Lagre på enhet» / Filer / Galleri). Returnerer true ved
  // suksess eller bevisst avbrudd; false ved ekte feil (kalleren faller tilbake).
  const saveFileNative = async (file: File): Promise<boolean> => {
    try {
      const { Filesystem, Directory } = await import('@capacitor/filesystem');
      const { Share } = await import('@capacitor/share');
      const path = file.name;
      // ÉN writeFile med full base64. Chunket appendFile (1.0.274) korrumperte
      // fila («Del» ga uspillbar video, eier-funn). writeFile uten encoding
      // base64-dekoder korrekt (per plugin-doc). Blob-data virker kun på web
      // (ingen JS→native-bro for Blob), så base64 er nødvendig på Android.
      const b64 = await blobToBase64(file);
      await Filesystem.writeFile({ path, data: b64, directory: Directory.Cache, recursive: true });
      // DIAGNOSTIKK: legg en adb-pullbar kopi i External (getExternalFilesDir) så
      // fila kan inspiseres hvis den fortsatt er uspillbar (transfer vs. eksport).
      try {
        await Filesystem.copy({ from: path, directory: Directory.Cache, to: 'reisefilm-debug.mp4', toDirectory: Directory.External });
      } catch { /* diagnostikk-kopi valgfri */ }
      const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });
      // FILER deles via `files:[uri]` (ikke `url:` — det er for web-lenker og
      // fester ikke selve videoen). Capacitor gjør file:// → content:// via FileProvider.
      await Share.share({ title: 'Fricamp', text: t('replay.shareText', 'Reisefilm fra Fricamp'), files: [uri] });
      return true;
    } catch (e: any) {
      const raw = String(e?.message || e);
      const msg = raw.toLowerCase();
      if (msg.includes('cancel') || msg.includes('abort') || msg.includes('dismiss')) return true;
      console.error('[reisefilm] saveFileNative feilet:', raw); // logcat-bevis
      // Gjør feilen SYNLIG for brukeren (før: stille → «knappene virker ikke»).
      setErrorMsg('');
      setDoneMsg(t('replay.shareFailed', 'Kunne ikke dele/lagre videoen: {{msg}}', { msg: raw.slice(0, 120) }));
      return false;
    }
  };

  const downloadBlob = async (file: File) => {
    if (Capacitor.isNativePlatform()) {
      // Native: saveFileNative åpner delings-arket («Lagre på enhet»/Galleri/Filer).
      // Ved feil setter den en synlig melding → ikke prøv anker-nedlasting (blokkeres
      // i Android WebView og ville skjult feilen).
      if (await saveFileNative(file)) setDoneMsg(t('replay.downloaded', 'Lastet ned'));
      return;
    }
    // Web/desktop: klassisk anker-nedlasting.
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  // ── Komponer-panelets handlinger ───────────────────────────────────────────
  const toggleStop = (spotId: string) => {
    setStopEdits(prev => ({
      ...prev,
      [spotId]: { media: prev[spotId]?.media ?? [], excluded: !prev[spotId]?.excluded },
    }));
  };

  // Android-KRITISK: en File fra <input type=file>/galleri er backet av en
  // content://-URI med en MIDLERTIDIG lesetilgang. Leser vi bytene SENT (etter at
  // ruter/fliser er hentet, sekunder etter valget) er tilgangen ofte utløpt →
  // `file.arrayBuffer()` kaster NotReadableError → klippet dekodes ikke → «vises
  // ikke, bare kart» (enhets-verifisert rotårsak). Vi kopierer derfor bytene til
  // en nettleser-styrt Blob UMIDDELBART ved valg (mens tilgangen er fersk).
  // Chromium kan disk-backe store Blob-er, så minnet sprenges ikke. Feiler lesing
  // (skal ikke skje mens tilgangen er fersk) beholder vi original-fila (web/desktop
  // leser uansett fint).
  const stabilizeFile = async (file: File): Promise<File> => {
    try {
      const buf = await file.arrayBuffer();
      return new File([buf], file.name || 'media', { type: file.type, lastModified: file.lastModified });
    } catch {
      return file;
    }
  };

  // KRITISK for mange bilder (eier-funn: 40–50 bilder → treg + native-KRÆSJ). Et
  // galleri-bilde er ofte 4–12 MP (10–40 MB dekodet). Å beholde alle som fullstore
  // bytes + dekodede bitmaps sprengte minnet. Vi RE-KODER hvert bilde til en
  // kompakt JPEG (maks 1600 px, q 0.82 ≈ 150–350 KB) ÉN gang ved valg — rikelig for
  // både preview (~594 px) og eksport (~1024 px). Dekoding gjøres SEKVENSIELT av
  // kalleren (ikke 50 samtidige fullstore dekodinger). Opptaksdato (EXIF) må leses
  // FØR re-koding (som stripper EXIF).
  const compactImage = async (file: File): Promise<File> => {
    try {
      const bmp = await createImageBitmap(file);
      const MAXD = 1600;
      const scale = Math.min(1, MAXD / Math.max(bmp.width, bmp.height));
      const w = Math.max(1, Math.round(bmp.width * scale)), h = Math.max(1, Math.round(bmp.height * scale));
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const cx = c.getContext('2d');
      if (!cx) { bmp.close(); return stabilizeFile(file); }
      cx.drawImage(bmp, 0, 0, w, h); bmp.close();
      const blob: Blob = await new Promise(res => c.toBlob(b => res(b || file), 'image/jpeg', 0.82));
      return new File([blob], (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg', lastModified: file.lastModified });
    } catch {
      return stabilizeFile(file);
    }
  };

  // Kjør `task` over `items` med maks `limit` samtidige (bounded pool). Brukes til
  // ingest: nok parallellitet til å lese content://-grantene FØR de utløper, men
  // IKKE alle 45 filene på én gang (minne-spike → heng, eier-funn ved 45 filer).
  const mapLimit = async <T,>(items: T[], limit: number, task: (item: T, i: number) => Promise<void>) => {
    let next = 0;
    const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (next < items.length) {
        const i = next++;
        try { await task(items[i], i); } catch { /* hopp over enkeltfil */ }
      }
    });
    await Promise.all(workers);
  };

  // Behandler valgte filer med BEGRENSET parallellitet. Video: byte-kopi (stabilizeFile)
  // + dato fra MP4-metadata. Bilde: kompakt JPEG + dato fra EXIF. Registrerer url→fil.
  const ingestFiles = async (
    origs: File[],
    addItem: (item: StopMedia & { file: File; atMs?: number }) => void,
    onOne?: () => void,
  ) => {
    // content://-grant er MIDLERTIDIG. Tidligere leste vi ALLE bytes på én gang
    // (Promise.all) — grant-trygt, men ved 45 filer holdt vi ~1 GB rå-bytes samtidig
    // → appen hang. Nå: BEGRENSET parallellitet så grantene fanges raskt (godt under
    // utløp) UTEN å holde alle råfilene samtidig. Video først (få, grant-kritiske +
    // må uansett beholdes), så bilder som komprimeres og frigjør råbytene fortløpende.
    const done = () => onOne?.();
    // Vaktbikkje: ÉN fil skal ALDRI kunne fryse hele innlastingen (enhets-verifisert:
    // en video-metadata-lesing hang → «Laster inn …» ble aldri ferdig). Rekker ikke
    // fila på 20 s, hopper vi over den (fremdriften går videre uansett).
    const fileTO = (p: Promise<unknown>, ms = 20000) => Promise.race([p, new Promise(r => setTimeout(r, ms))]);
    const videos = origs.filter(o => o.type.startsWith('video/'));
    const photos = origs.filter(o => !o.type.startsWith('video/'));
    await mapLimit(videos, 6, async (orig) => {
      await fileTO((async () => {
        const s = await stabilizeFile(orig); // byte-kopi mens grant er ferskt
        // Opptaksdato AVVENTES her (ikke etterpå) — en sen setAtMs ville endret
        // tripMedia ETTER at brukeren trykket «Lag film» → preload-restart/heng.
        const ms = await mediaCaptureMs(s).catch(() => null);
        const url = URL.createObjectURL(s);
        mediaFilesRef.current.set(url, s);
        addItem({ kind: 'video', url, file: s, atMs: ms ?? undefined });
      })());
      done();
    });
    await mapLimit(photos, 5, async (orig) => {
      await fileTO((async () => {
        const photoMs = await mediaCaptureMs(orig).catch(() => null); // EXIF før compact stripper den
        const file = await compactImage(orig); // ~200 KB JPEG — råbytene frigjøres etterpå
        const url = URL.createObjectURL(file);
        mediaFilesRef.current.set(url, file);
        addItem({ kind: 'photo', url, file, atMs: photoMs ?? undefined });
      })());
      done();
    });
  };

  const addMedia = (spotId: string, files: FileList | null) => {
    if (!files?.length) return;
    const existing = stopEditsRef.current[spotId]?.media.length ?? 0;
    const picked = Array.from(files).slice(0, 6 - existing);
    void ingestFiles(
      picked,
      item => setStopEdits(prev => { const ex = prev[spotId] ?? { media: [] }; return { ...prev, [spotId]: { ...ex, media: [...ex.media, item] } }; }),
    );
  };

  const removeMedia = (spotId: string, url: string) => {
    URL.revokeObjectURL(url);
    mediaBitmapsRef.current.delete(url); // LRU lukker bitmap selv
    mediaFilesRef.current.delete(url);
    const ev = mediaFramesRef.current.get(url);
    if (ev) { for (const f of ev.frames) f.bitmap.close(); mediaFramesRef.current.delete(url); }
    const au = mediaAudioRef.current.get(url);
    if (au) { au.pause(); au.src = ''; mediaAudioRef.current.delete(url); }
    setStopEdits(prev => ({
      ...prev,
      [spotId]: { ...prev[spotId], media: (prev[spotId]?.media ?? []).filter(m => m.url !== url) },
    }));
  };

  const addTripMedia = (files: FileList | null) => {
    if (!files?.length) return;
    const picked = Array.from(files).slice(0, 60 - tripMediaRef.current.length);
    setIngest({ total: picked.length, done: 0 }); // lasteindikator: filer leses/komprimeres nå
    void ingestFiles(
      picked,
      item => setTripMedia(prev => [...prev, item]),
      () => setIngest(prev => (prev ? { ...prev, done: prev.done + 1 } : prev)),
    ).finally(() => setIngest(null));
  };

  const removeTripMedia = (url: string) => {
    URL.revokeObjectURL(url);
    mediaBitmapsRef.current.delete(url); // LRU lukker bitmap selv
    mediaFilesRef.current.delete(url);
    const ev = mediaFramesRef.current.get(url);
    if (ev) { for (const f of ev.frames) f.bitmap.close(); mediaFramesRef.current.delete(url); }
    const au = mediaAudioRef.current.get(url);
    if (au) { au.pause(); au.src = ''; mediaAudioRef.current.delete(url); }
    setTripMedia(prev => prev.filter(m => m.url !== url));
  };

  // ── Utkast: lagre + gjenoppta ──────────────────────────────────────────────
  /** Lagrer gjeldende arbeid som utkast: innstillinger + KOPI av valgte klipp/
   *  bilder (og ev. egen lyd) i lokal IndexedDB. Filene lastes aldri opp. */
  const saveCurrentDraft = async () => {
    if (draftBusy) return;
    setDraftBusy(true); setDraftMsg('');
    try {
      const media: ReplayDraft['media'] = [];
      for (const m of tripMediaRef.current) media.push({ scope: 'trip', kind: m.kind, name: m.file.name || 'klipp', type: m.file.type, atMs: m.atMs, blob: m.file });
      for (const [spotId, edit] of Object.entries(stopEditsRef.current)) {
        for (const m of edit.media) media.push({ scope: 'stop', spotId, kind: m.kind, name: m.file.name || 'klipp', type: m.file.type, blob: m.file });
      }
      if (musicMode === 'custom' && customAudioRef.current) {
        media.push({ scope: 'audio', kind: 'photo', name: customAudioRef.current.name || 'lyd', type: customAudioRef.current.type, blob: customAudioRef.current });
      }
      const stopExcludes: Record<string, boolean> = {};
      for (const [spotId, edit] of Object.entries(stopEditsRef.current)) if (edit.excluded) stopExcludes[spotId] = true;
      const now = Date.now();
      const draft: ReplayDraft = {
        id: makeDraftId(now),
        name: tripTitle || formatVisitDateRange(filmFrom ? new Date(`${filmFrom}T00:00:00`).getTime() : now, undefined),
        updatedAt: now,
        settings: {
          filmFrom, filmTo, clipStyle, tempo, orientation, hideNames, format,
          musicMode, selectedTrack, musicOverride, customAudioName, stopExcludes,
        },
        media,
      };
      await saveDraft(draft);
      refreshDrafts();
      setDraftMsg(t('replay.draft.saved', 'Utkast lagret'));
      setTimeout(() => setDraftMsg(''), 2500);
    } catch {
      setDraftMsg(t('replay.draft.saveFailed', 'Kunne ikke lagre utkast'));
    } finally {
      setDraftBusy(false);
    }
  };

  /** Gjenoppretter et utkast: bygger File-objekter fra de kopierte bytene og
   *  setter alle innstillinger, så hopper rett til film-steget. */
  const resumeDraft = async (id: string) => {
    if (draftBusy) return;
    setDraftBusy(true); setDraftMsg('');
    try {
      const draft = await getDraft(id);
      if (!draft) { setDraftMsg(t('replay.draft.loadFailed', 'Kunne ikke åpne utkast')); return; }
      const s = draft.settings as Record<string, any>;
      // Rydd nåværende media-URL-er før vi bytter (unngå lekkasje).
      for (const m of tripMediaRef.current) URL.revokeObjectURL(m.url);
      for (const edit of Object.values(stopEditsRef.current)) for (const m of edit.media) URL.revokeObjectURL(m.url);

      const newTrip: Array<StopMedia & { file: File; atMs?: number }> = [];
      const newStops: Record<string, StopEdit> = {};
      for (const spotId of Object.keys((s.stopExcludes as Record<string, boolean>) || {})) {
        newStops[spotId] = { excluded: true, media: [] };
      }
      for (const dm of draft.media) {
        const file = new File([dm.blob], dm.name, { type: dm.type });
        const url = URL.createObjectURL(file);
        if (dm.scope === 'trip') {
          newTrip.push({ kind: dm.kind, url, file, atMs: dm.atMs });
        } else if (dm.scope === 'stop' && dm.spotId) {
          const ex = newStops[dm.spotId] ?? { media: [] };
          ex.media = [...ex.media, { kind: dm.kind, url, file }];
          newStops[dm.spotId] = ex;
        } else if (dm.scope === 'audio') {
          customAudioRef.current = file;
        }
      }

      if (typeof s.filmFrom === 'string') setFilmFrom(s.filmFrom);
      if (typeof s.filmTo === 'string') setFilmTo(s.filmTo);
      if (s.clipStyle && CLIP_STYLES[s.clipStyle as ClipStyle]) setClipStyle(s.clipStyle);
      if (s.tempo) setTempo(s.tempo);
      if (s.orientation && ORIENTATIONS[s.orientation as Orientation]) setOrientation(s.orientation);
      if (typeof s.hideNames === 'boolean') setHideNames(s.hideNames);
      if (s.format) setFormat(s.format);
      if (s.musicMode) setMusicMode(s.musicMode);
      if ('selectedTrack' in s) setSelectedTrack(s.selectedTrack ?? null);
      if ('musicOverride' in s) setMusicOverride(s.musicOverride ?? null);
      if (typeof s.customAudioName === 'string') setCustomAudioName(s.customAudioName);
      setStopEdits(newStops);
      setTripMedia(newTrip);
      setWizardStep('film');
      setComposeOpen(false);
    } catch {
      setDraftMsg(t('replay.draft.loadFailed', 'Kunne ikke åpne utkast'));
    } finally {
      setDraftBusy(false);
    }
  };

  const removeDraft = async (id: string) => {
    try { await deleteDraft(id); } catch { /* ignorer */ }
    refreshDrafts();
  };

  /** Verifisering: trykk på et stopp → hent plassens bilder (én gang). */
  const toggleExpandSpot = (spotId: string) => {
    setExpandedSpotId(prev => (prev === spotId ? null : spotId));
    if (spotImages[spotId]) return;
    setSpotImages(prev => ({ ...prev, [spotId]: 'loading' }));
    (async () => {
      const spot = spots.find(sp => sp.id === spotId);
      const local = [...(spot?.photoUrls ?? []), ...(spot?.auto_image_url ? [spot.auto_image_url] : [])];
      let fetched: string[] = [];
      try { fetched = await getSpotImages(spotId); } catch { /* lokale bilder holder */ }
      const all = [...new Set([...local, ...fetched])].slice(0, 6);
      setSpotImages(prev => ({ ...prev, [spotId]: all }));
    })();
  };

  const spotById = useMemo(() => new Map(spots.map(sp => [sp.id, sp])), [spots]);
  // Mellomspill-visning i panelet: afterStopIndex (i effectiveSpec) → spotId.
  const interludeAfterSpotId = useMemo(() => {
    const map: Record<string, StopMedia[]> = {};
    for (const inter of effectiveSpec.interludes ?? []) {
      const spot = effectiveSpec.stops[inter.afterStopIndex];
      if (spot) map[spot.spotId] = inter.media;
    }
    return map;
  }, [effectiveSpec]);

  const includedCount = effectiveSpec.stops.length;

  // Forhåndsvisningens bredde klemmes mot viewport-HØYDEN (bredde × format gir
  // canvas-høyden) slik at kontrollene og «Lagre som video» alltid får plass
  // uten scrolling. Budsjettet er målt høyde på kontrollstabelen + padding og
  // avhenger av om «Flere valg» er utvidet. Inline style (ikke Tailwind-klasse)
  // fordi verdien er dynamisk.
  const previewRatio = orientation === '16:9' ? 1.7778 : orientation === '1:1' ? 1 : 0.5625;
  const previewCapPx = orientation === '16:9' ? 560 : orientation === '1:1' ? 400 : 340;
  const previewFloorPx = orientation === '16:9' ? 200 : orientation === '1:1' ? 160 : 140;
  const previewMaxWidth = `min(${previewCapPx}px, max(${previewFloorPx}px, (100svh - ${showAdvanced ? 30 : 19}rem) * ${previewRatio}))`;

  // ── UI ─────────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-modal bg-[var(--bg-base)] flex flex-col items-center p-4 overflow-y-auto">
      <button
        type="button"
        onClick={onClose}
        aria-label={t('replay.close', 'Lukk')}
        className="absolute top-[var(--header-clear)] right-4 p-3 rounded-full bg-[var(--bg-surface)] border border-[var(--border-primary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
      >
        <X size={18} />
      </button>

      {wizardStep === 'date' && (
        <div className="w-full max-w-[400px] my-auto space-y-4">
          <div>
            <h2 className="text-lg font-black text-[var(--text-primary)]">{t('replay.wizard.dateTitle', 'Velg periode')}</h2>
            <p className="text-2xs text-[var(--text-muted)] mt-1">{t('replay.wizard.dateHint', 'Vi har valgt din siste tur. Juster datoene om du vil ha en annen periode.')}</p>
          </div>
          <div className="flex gap-2">
            {([
              { key: 'day' as const, label: t('replay.period.lastDay', 'Siste dag') },
              { key: 'trip' as const, label: t('replay.period.lastTrip', 'Siste tur') },
              { key: 'all' as const, label: t('replay.period.all', 'Hele reisen') },
            ]).map(p => (
              <button
                key={p.key}
                type="button"
                onClick={() => applyPeriodPreset(p.key)}
                className={`flex-1 py-2 rounded-xl text-3xs font-black uppercase tracking-widest border transition-colors ${
                  activePreset === p.key
                    ? 'bg-brand/15 border-brand/50 text-brand'
                    : 'bg-[var(--bg-input)] border-[var(--border-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-2xl px-3 py-3">
            <CalendarRange size={16} className="text-[var(--text-muted)] flex-shrink-0" />
            <input
              type="date"
              value={filmFrom}
              max={filmTo || undefined}
              onChange={e => setFilmFrom(e.target.value)}
              aria-label={t('visitsTab.filter.from', 'Fra dato')}
              className="flex-1 min-w-0 bg-transparent text-tiny text-[var(--text-primary)] outline-none [color-scheme:dark]"
            />
            <span className="text-2xs font-black text-[var(--text-muted)]">-</span>
            <input
              type="date"
              value={filmTo}
              min={filmFrom || undefined}
              onChange={e => setFilmTo(e.target.value)}
              aria-label={t('visitsTab.filter.to', 'Til dato')}
              className="flex-1 min-w-0 bg-transparent text-tiny text-[var(--text-primary)] outline-none [color-scheme:dark]"
            />
          </div>
          <p className="text-2xs text-center text-[var(--text-muted)]">
            {isViableTrip(baseSpec)
              ? t('replay.wizard.stopCount', '{{count}} stopp i filmen', { count: baseSpec.stops.length })
              : t('visitsTab.replay.needTwo', 'Velg en periode med minst 2 innsjekk med posisjon')}
          </p>
          <button
            type="button"
            onClick={() => setWizardStep('media')}
            disabled={!isViableTrip(baseSpec)}
            className="w-full flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark disabled:bg-[var(--bg-input)] disabled:text-[var(--text-muted)] text-stone-900 py-3.5 rounded-xl text-tiny font-black uppercase tracking-widest transition-colors"
          >
            {t('replay.wizard.next', 'Neste')} <ChevronRight size={16} />
          </button>

          {/* Lagrede utkast (eier-ønske): gjenoppta arbeid uten å re-velge klipp. */}
          {drafts.length > 0 && (
            <div className="pt-3 border-t border-[var(--border-secondary)]">
              <p className="text-3xs font-black uppercase tracking-widest text-[var(--text-muted)] mb-2">
                {t('replay.draft.savedTitle', 'Lagrede utkast')}
              </p>
              <div className="space-y-1.5">
                {drafts.map(d => (
                  <div key={d.id} className="flex items-center gap-2 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-3 py-2">
                    <button
                      type="button"
                      disabled={draftBusy}
                      onClick={() => resumeDraft(d.id)}
                      className="flex-1 flex items-center gap-2 text-left min-w-0 disabled:opacity-50"
                    >
                      <Play size={15} className="text-brand flex-shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-2xs font-bold text-[var(--text-primary)] truncate">{d.name || t('replay.draft.untitled', 'Uten navn')}</span>
                        <span className="block text-3xs text-[var(--text-muted)]">
                          {t('replay.draft.clipCount', '{{count}} klipp', { count: d.mediaCount })} · {(d.totalBytes / 1048576).toFixed(1)} MB
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => removeDraft(d.id)}
                      aria-label={t('replay.draft.delete', 'Slett utkast')}
                      className="p-1.5 text-[var(--text-muted)] hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {wizardStep === 'media' && (
        <div className="w-full max-w-[400px] my-auto flex flex-col gap-3 max-h-[80vh]">
          <div>
            <h2 className="text-lg font-black text-[var(--text-primary)]">{t('replay.wizard.mediaTitle', 'Legg til egne bilder og video')}</h2>
            <p className="text-2xs text-[var(--text-muted)] mt-1">{t('replay.wizard.mediaHint', 'Velg bilder og videoklipp fra turen - de flettes automatisk inn i filmen etter naar de ble tatt. Ingenting lastes opp.')}</p>
          </div>
          <label className={`w-full min-h-11 flex items-center justify-center gap-2 py-3 rounded-xl text-tiny font-black uppercase tracking-widest bg-brand/10 border border-brand/40 text-brand ${ingest ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}>
            <Plus size={15} /> {t('replay.compose.addTrip', 'Legg til bilder/video fra turen')}
            <input
              type="file"
              accept="image/*,video/mp4,video/quicktime,video/webm"
              multiple
              disabled={!!ingest}
              className="hidden"
              onChange={e => { addTripMedia(e.target.files); e.target.value = ''; }}
            />
          </label>
          {ingest && (
            <div className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-[var(--bg-input)] border border-[var(--border-primary)] text-2xs font-black uppercase tracking-widest text-[var(--text-secondary)]">
              <Loader2 size={14} className="animate-spin text-brand" />
              {t('replay.compose.loadingMedia', 'Laster inn {{done}}/{{total}} …', { done: ingest.done, total: ingest.total })}
            </div>
          )}
          {!!tripMedia.length && (
            <div className="flex gap-2 flex-wrap overflow-y-auto">
              {tripMedia.map(m => (
                <div key={m.url} className="relative w-16 h-16 rounded-lg overflow-hidden border border-[var(--border-primary)] bg-[var(--bg-input)]">
                  {m.kind === 'photo' ? (
                    <img src={m.url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  ) : (
                    // Ikke autoPlay: 20+ samtidig spillende videoer i rutenettet kokte CPU/minne
                    // (eier-funn ved mange filer). preload=metadata viser første ramme uten avspilling.
                    <video src={m.url} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                  )}
                  <button
                    type="button"
                    onClick={() => removeTripMedia(m.url)}
                    aria-label={t('replay.compose.remove', 'Fjern')}
                    className="absolute top-0.5 right-0.5 p-0.5 rounded bg-black/70 text-white"
                  >
                    <Trash2 size={10} />
                  </button>
                  {m.kind === 'video' && (
                    <span className="absolute bottom-0.5 left-0.5 text-[8px] font-black bg-black/70 text-white px-1 rounded">VIDEO</span>
                  )}
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={() => setComposeOpen(true)}
            className="w-full min-h-11 flex items-center justify-center gap-1.5 py-2 rounded-xl text-2xs font-black uppercase tracking-widest bg-[var(--bg-input)] border border-[var(--border-primary)] text-[var(--text-secondary)]"
          >
            <SlidersHorizontal size={13} /> {t('replay.wizard.perStop', 'Sjekk stopp og bilder per plass')}
          </button>
          <div className="flex items-center gap-2 mt-1">
            <button
              type="button"
              onClick={() => setWizardStep('date')}
              className="min-h-11 flex items-center justify-center gap-1 px-4 rounded-xl text-2xs font-black uppercase tracking-widest bg-[var(--bg-input)] border border-[var(--border-primary)] text-[var(--text-muted)]"
            >
              {t('replay.wizard.back', 'Tilbake')}
            </button>
            <button
              type="button"
              disabled={!!ingest}
              onClick={() => { if (!ingest) setWizardStep('film'); }}
              className={`flex-1 flex items-center justify-center gap-2 bg-brand text-stone-900 py-3.5 rounded-xl text-tiny font-black uppercase tracking-widest transition-colors ${ingest ? 'opacity-50 pointer-events-none' : 'hover:bg-brand-dark'}`}
            >
              {ingest
                ? <><Loader2 size={16} className="animate-spin" /> {t('replay.wizard.loadingShort', 'Laster inn …')}</>
                : <><Film size={16} /> {t('replay.wizard.make', 'Lag film')}</>}
            </button>
          </div>
        </div>
      )}

      {wizardStep === 'film' && (
      <div className="relative w-full mt-auto" style={{ maxWidth: previewMaxWidth }}>
        <canvas
          ref={canvasRef}
          width={pw}
          height={ph}
          className="w-full rounded-card border border-[var(--border-primary)] bg-[var(--bg-surface)]"
          style={{ aspectRatio: `${dims.w} / ${dims.h}` }}
        />

        {phase === 'preloading' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-card bg-[var(--bg-base)]/90">
            <Loader2 size={30} className="animate-spin text-brand" />
            <p className="text-tiny font-bold text-[var(--text-secondary)]">{t('replay.preparing', 'Genererer reisefilm …')}</p>
            <p className="text-lg font-black text-brand tabular-nums leading-none">{Math.round(progress * 100)}%</p>
            <div className="w-2/3 h-1.5 bg-[var(--bg-input)] rounded-full overflow-hidden">
              <div className="h-full bg-brand rounded-full transition-[width]" style={{ width: `${Math.round(progress * 100)}%` }} />
            </div>
          </div>
        )}

        {phase === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-card bg-[var(--bg-base)]/90 p-6 text-center">
            <p className="text-tiny text-[var(--text-secondary)]">{errorMsg || t('replay.error.generic', 'Noe gikk galt. Prøv igjen.')}</p>
            <button
              type="button"
              onClick={() => { setErrorMsg(''); setRetryToken(x => x + 1); }}
              className="flex items-center gap-2 bg-brand text-stone-900 px-4 py-2.5 rounded-xl text-tiny font-black uppercase tracking-widest"
            >
              <RefreshCw size={14} /> {t('replay.retry', 'Prøv igjen')}
            </button>
          </div>
        )}

        {/* Play/pause for forhåndsvisningen (eier-ønske) */}
        {(phase === 'ready' || phase === 'done') && (
          <button
            type="button"
            onClick={togglePause}
            aria-label={paused ? t('replay.play', 'Spill av') : t('replay.pause', 'Pause')}
            className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center justify-center w-12 h-12 rounded-full bg-black/55 backdrop-blur-sm text-white border border-white/20 hover:bg-black/70 transition-colors"
          >
            {paused ? <Play size={20} className="translate-x-0.5" /> : <Pause size={20} />}
          </button>
        )}
      </div>
      )}

      {wizardStep === 'film' && (
      <div className={`w-full mt-4 mb-auto space-y-2 ${orientation === '16:9' ? 'max-w-[560px]' : orientation === '1:1' ? 'max-w-[400px]' : 'max-w-[340px]'}`}>
        {skippedNoCoords > 0 && (
          <p className="text-2xs text-[var(--text-muted)] text-center">
            {t('replay.skippedNoCoords', '{{count}} besøk uten posisjon vises ikke i filmen', { count: skippedNoCoords })}
          </p>
        )}

        {(phase === 'ready' || phase === 'done') && (
          <>
            {/* Stilpakke = HOVEDVALGET (eier: ikke eksponer flere valg enn
                stilpakken). Hver pakke vises med en liten animert forhåndsvisning
                («gif») så uttrykket sees før valg. Alt annet ligger bak «Tilpass». */}
            <div className="grid grid-cols-4 gap-1.5">
              {(Object.keys(CLIP_STYLES) as ClipStyle[]).map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setClipStyle(s)}
                  aria-label={STYLE_LABELS[s] ?? s}
                  className={`relative rounded-xl overflow-hidden border-2 transition ${clipStyle === s ? 'border-brand' : 'border-transparent opacity-75 hover:opacity-100'}`}
                >
                  <div className="aspect-[3/4] w-full bg-[var(--bg-input)]">
                    <StylePreviewCanvas styleKey={s} />
                  </div>
                  <span className="absolute bottom-0 inset-x-0 py-0.5 px-0.5 text-[9px] font-black uppercase tracking-wider text-white bg-black/50 text-center truncate">
                    {STYLE_LABELS[s] ?? s}
                  </span>
                  {clipStyle === s && (
                    <span className="absolute top-1 right-1 text-brand"><CheckCircle2 size={14} /></span>
                  )}
                </button>
              ))}
            </div>

            {/* ÉN inngang til ALT annet (musikk, format, tempo, komponer …) */}
            {!showAdvanced ? (
              <button
                type="button"
                onClick={() => setShowAdvanced(true)}
                className="w-full min-h-11 flex items-center justify-center gap-1.5 py-2 rounded-xl text-2xs font-black uppercase tracking-widest bg-[var(--bg-input)] border border-[var(--border-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              >
                <SlidersHorizontal size={13} /> {t('replay.customize', 'Tilpass')}
              </button>
            ) : (
              <>
                {/* Musikk (nå bak Tilpass) */}
                {format === 'mp4' && (
                  <button
                    type="button"
                    onClick={() => setMusicSheetOpen(true)}
                    className="w-full min-h-11 flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-[var(--bg-input)] border border-[var(--border-primary)] text-left"
                  >
                    <span className="flex items-center gap-2 text-2xs font-bold text-[var(--text-secondary)] truncate">
                      ♪ {musicMode === 'off'
                        ? t('replay.musicMode.off', 'Av')
                        : musicMode === 'track'
                          ? (selectedTrack?.title || t('replay.musicMode.track', 'Ferdig spor'))
                          : musicMode === 'custom'
                            ? (customAudioName || t('replay.musicMode.custom', 'Egen lyd'))
                            : musicOverride
                              ? t(`replay.mood.${musicOverride}`, musicOverride)
                              : t('replay.musicAuto', 'Følger stilen')}
                    </span>
                    <span className="text-2xs font-black uppercase tracking-widest text-brand flex-shrink-0">
                      {t('replay.change', 'Bytt')}
                    </span>
                  </button>
                )}

                <div className="flex items-center gap-2">
                  <div className="flex flex-1 bg-[var(--bg-input)] p-1 rounded-xl border border-[var(--border-primary)]">
                    {(Object.keys(ORIENTATIONS) as Orientation[]).map(o => (
                      <button
                        key={o}
                        type="button"
                        onClick={() => setOrientation(o)}
                        className={`flex-1 min-h-11 py-1.5 rounded-lg text-2xs font-black tracking-widest transition ${orientation === o ? 'bg-brand text-stone-900' : 'text-[var(--text-muted)]'}`}
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                  <div className="flex bg-[var(--bg-input)] p-1 rounded-xl border border-[var(--border-primary)]">
                    {(['mp4', 'gif'] as ExportFormat[]).map(f => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => setFormat(f)}
                        className={`min-h-11 px-3 py-1.5 rounded-lg text-2xs font-black tracking-widest uppercase transition ${format === f ? 'bg-brand text-stone-900' : 'text-[var(--text-muted)]'}`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tempo (eier-feedback: roligere film) */}
                <div className="flex bg-[var(--bg-input)] p-1 rounded-xl border border-[var(--border-primary)]">
                  {(['kort', 'middels', 'lang'] as const).map(tp => (
                    <button
                      key={tp}
                      type="button"
                      onClick={() => setTempo(tp)}
                      className={`flex-1 min-h-11 py-1.5 rounded-lg text-2xs font-black tracking-widest uppercase transition ${tempo === tp ? 'bg-brand text-stone-900' : 'text-[var(--text-muted)]'}`}
                    >
                      {tp === 'kort' ? t('replay.tempo.kort', 'Rask') : tp === 'lang' ? t('replay.tempo.lang', 'Rolig') : t('replay.tempo.middels', 'Middels')}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setHideNames(v => !v)}
                    className={`flex-1 min-h-11 flex items-center justify-center gap-1.5 py-2 rounded-xl text-2xs font-black uppercase tracking-widest border transition-colors ${hideNames ? 'bg-brand/15 border-brand/40 text-brand' : 'bg-[var(--bg-input)] border-[var(--border-primary)] text-[var(--text-muted)]'}`}
                  >
                    <EyeOff size={13} /> {t('replay.hideNames', 'Skjul stedsnavn')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setComposeOpen(true)}
                    className="flex-1 min-h-11 flex items-center justify-center gap-1.5 py-2 rounded-xl text-2xs font-black uppercase tracking-widest bg-[var(--bg-input)] border border-[var(--border-primary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                  >
                    <Plus size={13} /> {t('replay.compose.open', 'Komponer')}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setShowAdvanced(false)}
                  className="w-full py-1 text-3xs font-black uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
                >
                  {t('replay.lessOptions', 'Skjul valg')}
                </button>
              </>
            )}
          </>
        )}

        {(phase === 'ready' || phase === 'exporting') && (
          <button
            type="button"
            onClick={exportVideo}
            disabled={phase === 'exporting'}
            className="w-full flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark text-stone-900 py-3.5 rounded-xl text-tiny font-black uppercase tracking-widest transition-colors disabled:opacity-60"
          >
            {phase === 'exporting' ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {t('replay.exporting', 'Lager video …')} {Math.round(progress * 100)}%
              </>
            ) : (
              <>
                <Download size={16} /> {format === 'gif' ? t('replay.exportGif', 'Lagre som GIF') : t('replay.export', 'Lagre som video')}
              </>
            )}
          </button>
        )}

        {/* Lagre som utkast (eier-ønske): ta vare på arbeidet + klippene lokalt
            og gjenoppta senere fra periode-steget. */}
        {phase === 'ready' && (
          <button
            type="button"
            onClick={saveCurrentDraft}
            disabled={draftBusy}
            className="w-full min-h-11 flex items-center justify-center gap-1.5 py-2 rounded-xl text-2xs font-black uppercase tracking-widest bg-[var(--bg-input)] border border-[var(--border-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors disabled:opacity-50"
          >
            {draftBusy
              ? <><Loader2 size={13} className="animate-spin" /> {t('replay.draft.saving', 'Lagrer utkast …')}</>
              : draftMsg
                ? <><CheckCircle2 size={13} className="text-brand" /> {draftMsg}</>
                : <><Plus size={13} /> {t('replay.draft.save', 'Lagre som utkast')}</>}
          </button>
        )}

        {phase === 'exporting' && (
          <div className="mt-2" role="status" aria-live="polite">
            <div className="h-2 w-full rounded-full bg-[var(--bg-input)] overflow-hidden">
              <div className="h-full bg-brand rounded-full transition-[width]" style={{ width: `${Math.round(progress * 100)}%` }} />
            </div>
            <p className="mt-1.5 text-2xs font-bold text-[var(--text-muted)] text-center">
              {progress >= 0.9 ? t('replay.exportingAudio', 'Legger på lyd …') : t('replay.exportingFrames', 'Bygger videobilder …')} {Math.round(progress * 100)}%
            </p>
          </div>
        )}

        {phase === 'done' && (
          <div className="space-y-2">
            <p className="text-tiny font-bold text-[var(--text-secondary)] text-center flex items-center justify-center gap-1.5">
              <CheckCircle2 size={14} className="text-brand" /> {doneMsg}
            </p>
            <div className="flex gap-2">
              {(Capacitor.isNativePlatform() || !!navigator.canShare) && (
                <button
                  type="button"
                  onClick={shareExported}
                  className="flex-1 flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark text-stone-900 py-3.5 rounded-xl text-tiny font-black uppercase tracking-widest transition-colors"
                >
                  <Share2 size={16} /> {t('replay.share', 'Del')}
                </button>
              )}
              <button
                type="button"
                onClick={() => exportedFileRef.current && downloadBlob(exportedFileRef.current)}
                className="flex-1 flex items-center justify-center gap-2 bg-[var(--bg-input)] border border-[var(--border-primary)] text-[var(--text-primary)] py-3.5 rounded-xl text-tiny font-black uppercase tracking-widest transition-colors"
              >
                <Download size={16} /> {t('replay.download', 'Last ned')}
              </button>
              <button
                type="button"
                onClick={exportVideo}
                aria-label={t('replay.exportAgain', 'lagre på nytt')}
                className="flex items-center justify-center px-3 rounded-xl bg-[var(--bg-input)] border border-[var(--border-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              >
                <RefreshCw size={15} />
              </button>
            </div>
          </div>
        )}

        {phase === 'preloading' && (
          <div className="w-full flex items-center justify-center gap-2 text-[var(--text-muted)] py-3.5 text-tiny font-black uppercase tracking-widest">
            <Film size={16} /> Fricamp
          </div>
        )}
      </div>
      )}

      {/* Fase 3: Komponer-panelet — velg stopp + egne bilder/videoklipp */}
      {composeOpen && (
        <div className="fixed inset-0 z-max bg-[var(--bg-base)]/95 backdrop-blur-sm flex items-end md:items-center justify-center">
          <div className="w-full max-w-md max-h-[85vh] bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-t-panel md:rounded-panel p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-black text-[var(--text-primary)]">{t('replay.compose.title', 'Komponer reisen')}</h3>
              <button
                type="button"
                onClick={() => setComposeOpen(false)}
                aria-label={t('replay.close', 'Lukk')}
                className="p-2 rounded-full bg-[var(--bg-surface-alt)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-2xs text-[var(--text-muted)]">
              {t('replay.compose.hint2', 'Velg stopp, sjekk at plassene stemmer (trykk for bilder), og legg til egne bilder/videoklipp - de flettes inn i filmen etter naar de ble tatt. Ingenting lastes opp.')}
            </p>

            {/* Tur-nivaa: medier flettes kronologisk mellom stoppene (CapCut-flyten) */}
            <label className="w-full min-h-11 flex items-center justify-center gap-2 py-2 rounded-xl text-2xs font-black uppercase tracking-widest bg-brand/10 border border-brand/40 text-brand cursor-pointer">
              <Plus size={13} /> {t('replay.compose.addTrip', 'Legg til bilder/video fra turen')}
              <input
                type="file"
                accept="image/*,video/mp4,video/quicktime,video/webm"
                multiple
                className="hidden"
                onChange={e => { addTripMedia(e.target.files); e.target.value = ''; }}
              />
            </label>

            <div className="flex-1 min-h-0 overflow-y-auto space-y-2">
              {baseSpec.stops.map((s, i) => {
                const excluded = !!stopEdits[s.spotId]?.excluded;
                const spot = spotById.get(s.spotId);
                const place = [s.county, spot?.municipality].filter(Boolean).join(' - ');
                const expanded = expandedSpotId === s.spotId;
                const images = spotImages[s.spotId];
                const interMedia = interludeAfterSpotId[s.spotId];
                const effStop = effectiveSpec.stops.find(x => x.spotId === s.spotId);
                const shownMedia = effStop?.media ?? [];
                const removeAny = (url: string) => {
                  if (tripMedia.some(m => m.url === url)) removeTripMedia(url);
                  else removeMedia(s.spotId, url);
                };
                return (
                  <React.Fragment key={s.spotId}>
                    <div className={`rounded-2xl border p-3 space-y-2 ${excluded ? 'border-[var(--border-primary)] opacity-50' : 'border-[var(--border-secondary)] bg-[var(--bg-surface-alt)]'}`}>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!excluded}
                          onChange={() => toggleStop(s.spotId)}
                          aria-label={s.name}
                          className="w-4 h-4 accent-brand-dark"
                        />
                        <button
                          type="button"
                          onClick={() => toggleExpandSpot(s.spotId)}
                          className="flex-1 min-w-0 text-left"
                        >
                          <p className="text-[12px] font-bold text-[var(--text-primary)] truncate">{i + 1}. {s.name}</p>
                          <p className="text-3xs text-[var(--text-muted)] truncate">
                            {formatVisitDateRange(s.visitedAt, s.checkedOutAt)}{place ? ` - ${place}` : ''}
                          </p>
                        </button>
                        {!excluded && (
                          <label className="flex items-center gap-1 text-3xs font-black uppercase tracking-widest text-brand cursor-pointer flex-shrink-0">
                            <Plus size={12} /> {t('replay.compose.addMedia', 'Bilder/video')}
                            <input
                              type="file"
                              accept="image/*,video/mp4,video/quicktime,video/webm"
                              multiple
                              className="hidden"
                              onChange={e => { addMedia(s.spotId, e.target.files); e.target.value = ''; }}
                            />
                          </label>
                        )}
                      </div>

                      {/* Verifisering: bilder av plassen (trykk paa navnet) */}
                      {expanded && (
                        <div className="flex gap-2 overflow-x-auto pb-1">
                          {images === 'loading' || !images ? (
                            <div className="flex items-center gap-2 text-3xs text-[var(--text-muted)] py-2">
                              <Loader2 size={12} className="animate-spin" /> {t('replay.compose.loadingPhotos', 'Henter bilder ...')}
                            </div>
                          ) : images.length ? (
                            images.map(url => (
                              <img key={url} src={url} alt="" loading="lazy" className="w-20 h-20 rounded-lg object-cover flex-shrink-0 border border-[var(--border-primary)]" />
                            ))
                          ) : (
                            <p className="text-3xs text-[var(--text-muted)] py-2">{t('replay.compose.noPhotos', 'Ingen bilder av plassen ennaa')}</p>
                          )}
                        </div>
                      )}

                      {!excluded && !!shownMedia.length && (
                        <div className="flex gap-2 flex-wrap">
                          {shownMedia.map(m => (
                            <div key={m.url} className="relative w-14 h-14 rounded-lg overflow-hidden border border-[var(--border-primary)] bg-[var(--bg-input)]">
                              {m.kind === 'photo' ? (
                                <img src={m.url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                              ) : (
                                <video src={m.url} muted autoPlay loop playsInline preload="metadata" className="w-full h-full object-cover" />
                              )}
                              <button
                                type="button"
                                onClick={() => removeAny(m.url)}
                                aria-label={t('replay.compose.remove', 'Fjern')}
                                className="absolute top-0.5 right-0.5 p-0.5 rounded bg-black/70 text-white"
                              >
                                <Trash2 size={10} />
                              </button>
                              {m.kind === 'video' && (
                                <span className="absolute bottom-0.5 left-0.5 text-[8px] font-black bg-black/70 text-white px-1 rounded">VIDEO</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Mellomspill: medier tatt underveis til neste stopp */}
                    {!!interMedia?.length && (
                      <div className="ml-6 rounded-2xl border border-dashed border-[var(--border-secondary)] p-2.5 space-y-1.5 bg-[var(--bg-input)]/50">
                        <p className="text-3xs font-black uppercase tracking-widest text-[var(--text-muted)]">
                          {t('replay.compose.between', 'Underveis')} - {interMedia.length}
                        </p>
                        <div className="flex gap-2 flex-wrap">
                          {interMedia.map(m => (
                            <div key={m.url} className="relative w-12 h-12 rounded-lg overflow-hidden border border-[var(--border-primary)] bg-[var(--bg-input)]">
                              {m.kind === 'photo' ? (
                                <img src={m.url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                              ) : (
                                <video src={m.url} muted autoPlay loop playsInline preload="metadata" className="w-full h-full object-cover" />
                              )}
                              <button
                                type="button"
                                onClick={() => removeTripMedia(m.url)}
                                aria-label={t('replay.compose.remove', 'Fjern')}
                                className="absolute top-0.5 right-0.5 p-0.5 rounded bg-black/70 text-white"
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => setComposeOpen(false)}
              disabled={includedCount < 2}
              className="w-full bg-brand hover:bg-brand-dark disabled:bg-[var(--bg-input)] disabled:text-[var(--text-muted)] text-stone-900 py-3 rounded-xl text-tiny font-black uppercase tracking-widest transition-colors"
            >
              {includedCount < 2
                ? t('visitsTab.replay.needTwo', 'Velg en periode med minst 2 innsjekk med posisjon')
                : t('replay.compose.done', 'Ferdig')}
            </button>
          </div>
        </div>
      )}

      {/* Musikk-arket (CapCut-speiling: anbefalte stemninger m/ forhåndslytt,
          egen lydfil, av). Syntetisert musikk = lisensfri; egen fil = lokal. */}
      {musicSheetOpen && (
        <div className="fixed inset-0 z-max bg-[var(--bg-base)]/95 backdrop-blur-sm flex items-end md:items-center justify-center">
          <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-t-panel md:rounded-panel p-4 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-black text-[var(--text-primary)]">♪ {t('replay.music', 'Musikk')}</h3>
              <button
                type="button"
                onClick={() => { stopMusicPreview(); setMusicSheetOpen(false); }}
                aria-label={t('replay.close', 'Lukk')}
                className="p-2 rounded-full bg-[var(--bg-surface-alt)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                <X size={16} />
              </button>
            </div>

            <button
              type="button"
              onClick={() => { setMusicMode('style'); setMusicOverride(null); }}
              className={`w-full min-h-11 flex items-center justify-between px-3 py-2 rounded-xl border text-2xs font-bold transition-colors ${musicMode === 'style' && !musicOverride ? 'border-brand/50 bg-brand/10 text-[var(--text-primary)]' : 'border-[var(--border-primary)] bg-[var(--bg-input)] text-[var(--text-secondary)]'}`}
            >
              <span>{t('replay.musicAuto', 'Følger stilen')} · {t(`replay.mood.${style.music}`, style.music)}</span>
              {musicMode === 'style' && !musicOverride && <CheckCircle2 size={15} className="text-brand" />}
            </button>

            {ALL_MUSIC_STYLES.map(mood => (
              <div
                key={mood}
                className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-xl border transition-colors ${musicMode === 'style' && musicOverride === mood ? 'border-brand/50 bg-brand/10' : 'border-[var(--border-primary)] bg-[var(--bg-input)]'}`}
              >
                <button
                  type="button"
                  onClick={() => previewMood(mood)}
                  aria-label={t('replay.listen', 'Lytt')}
                  className="min-h-11 min-w-11 flex items-center justify-center rounded-lg text-brand"
                >
                  ▶
                </button>
                <button
                  type="button"
                  onClick={() => { setMusicMode('style'); setMusicOverride(mood); }}
                  className="flex-1 min-h-11 text-left text-2xs font-bold text-[var(--text-primary)]"
                >
                  {t(`replay.mood.${mood}`, mood)}
                </button>
                {musicMode === 'style' && musicOverride === mood && <CheckCircle2 size={15} className="text-brand flex-shrink-0" />}
              </div>
            ))}

            {BUNDLED_TRACKS.length > 0 && (
              <>
                <p className="text-3xs font-black uppercase tracking-widest text-[var(--text-muted)] px-1 pt-1">
                  {t('replay.tracksHeading', 'Ferdige spor')}
                </p>
                {BUNDLED_TRACKS.map(track => (
                  <div
                    key={track.url}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-xl border transition-colors ${musicMode === 'track' && selectedTrack?.url === track.url ? 'border-brand/50 bg-brand/10' : 'border-[var(--border-primary)] bg-[var(--bg-input)]'}`}
                  >
                    <button
                      type="button"
                      onClick={() => previewTrack(track.url)}
                      aria-label={t('replay.listen', 'Lytt')}
                      className="min-h-11 min-w-11 flex items-center justify-center rounded-lg text-brand"
                    >
                      ▶
                    </button>
                    <button
                      type="button"
                      onClick={() => { setMusicMode('track'); setSelectedTrack(track); }}
                      className="flex-1 min-h-11 text-left text-2xs font-bold text-[var(--text-primary)] truncate"
                    >
                      {track.title}
                    </button>
                    {musicMode === 'track' && selectedTrack?.url === track.url && <CheckCircle2 size={15} className="text-brand flex-shrink-0" />}
                  </div>
                ))}
              </>
            )}

            {savedAudio.length > 0 && (
              <>
                <p className="text-3xs font-black uppercase tracking-widest text-[var(--text-muted)] px-1 pt-1">
                  {t('replay.myAudio', 'Mine lyder')}
                </p>
                {savedAudio.map(rec => {
                  const active = musicMode === 'custom' && selectedSavedId === rec.id;
                  return (
                    <div
                      key={rec.id}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-xl border transition-colors ${active ? 'border-brand/50 bg-brand/10' : 'border-[var(--border-primary)] bg-[var(--bg-input)]'}`}
                    >
                      <button
                        type="button"
                        onClick={() => previewTrack(savedAudioUrl(rec))}
                        aria-label={t('replay.listen', 'Lytt')}
                        className="min-h-11 min-w-11 flex items-center justify-center rounded-lg text-brand"
                      >
                        ▶
                      </button>
                      <button
                        type="button"
                        onClick={() => selectSavedAudio(rec)}
                        className="flex-1 min-h-11 text-left text-2xs font-bold text-[var(--text-primary)] truncate"
                      >
                        {rec.name}
                      </button>
                      {active && <CheckCircle2 size={15} className="text-brand flex-shrink-0" />}
                      <button
                        type="button"
                        onClick={() => removeSavedAudio(rec)}
                        aria-label={t('replay.deleteAudio', 'Slett lyd')}
                        className="min-h-11 min-w-11 flex items-center justify-center rounded-lg text-[var(--text-muted)] hover:text-red-400"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  );
                })}
              </>
            )}

            <label className={`w-full min-h-11 flex items-center justify-between px-3 py-2 rounded-xl border text-2xs font-bold cursor-pointer transition-colors ${musicMode === 'custom' ? 'border-brand/50 bg-brand/10 text-[var(--text-primary)]' : 'border-dashed border-[var(--border-secondary)] bg-[var(--bg-input)] text-[var(--text-secondary)]'}`}>
              <span className="flex items-center gap-2 truncate"><Plus size={13} /> {t('replay.addAudio', 'Legg til lydfil (MP3/M4A/WAV) — lagres kun på enheten')}</span>
              {musicMode === 'custom' && <CheckCircle2 size={15} className="text-brand flex-shrink-0" />}
              <input
                type="file"
                accept="audio/*"
                className="hidden"
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) void handlePickCustomAudio(f);
                  e.target.value = '';
                }}
              />
            </label>

            {jamendoEnabled() && (
              <div className="space-y-1.5 pt-1">
                <p className="text-3xs font-black uppercase tracking-widest text-[var(--text-muted)] px-1">
                  {t('replay.searchMusic', 'Søk musikk')}
                </p>
                <div className="flex items-center gap-2 bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-xl px-3 py-2">
                  <input
                    type="text"
                    value={jamendoQuery}
                    onChange={e => setJamendoQuery(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') runJamendoSearch(); }}
                    placeholder={t('replay.searchPlaceholder', 'Søk etter stemning eller sjanger …')}
                    className="flex-1 min-w-0 bg-transparent text-2xs text-[var(--text-primary)] outline-none"
                  />
                  <button
                    type="button"
                    onClick={runJamendoSearch}
                    disabled={jamendoBusy}
                    className="text-2xs font-black uppercase tracking-widest text-brand disabled:opacity-50 flex-shrink-0"
                  >
                    {jamendoBusy ? '…' : t('replay.searchGo', 'Søk')}
                  </button>
                </div>
                {jamendoError && <p className="text-3xs text-red-400 px-1">{jamendoError}</p>}
                {jamendoResults.map(tr => {
                  const trackUrl = proxiedAudioUrl(tr.audioUrl);
                  const active = musicMode === 'track' && selectedTrack?.url === trackUrl;
                  return (
                    <div
                      key={tr.id}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-xl border transition-colors ${active ? 'border-brand/50 bg-brand/10' : 'border-[var(--border-primary)] bg-[var(--bg-input)]'}`}
                    >
                      <button
                        type="button"
                        onClick={() => previewTrack(trackUrl)}
                        aria-label={t('replay.listen', 'Lytt')}
                        className="min-h-11 min-w-11 flex items-center justify-center rounded-lg text-brand"
                      >
                        ▶
                      </button>
                      <button
                        type="button"
                        onClick={() => { setMusicMode('track'); setSelectedTrack({ url: trackUrl, title: tr.artist ? `${tr.title} — ${tr.artist}` : tr.title }); }}
                        className="flex-1 min-w-0 min-h-11 text-left"
                      >
                        <span className="block text-2xs font-bold text-[var(--text-primary)] truncate">{tr.title}</span>
                        {tr.artist && <span className="block text-3xs text-[var(--text-muted)] truncate">{tr.artist}</span>}
                      </button>
                      {active && <CheckCircle2 size={15} className="text-brand flex-shrink-0" />}
                    </div>
                  );
                })}
                <p className="text-3xs text-[var(--text-muted)] px-1">
                  {t('replay.jamendoCredit', 'Musikk via Jamendo — krediter artisten ved deling.')}
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={() => setMusicMode('off')}
              className={`w-full min-h-11 flex items-center justify-between px-3 py-2 rounded-xl border text-2xs font-bold transition-colors ${musicMode === 'off' ? 'border-brand/50 bg-brand/10 text-[var(--text-primary)]' : 'border-[var(--border-primary)] bg-[var(--bg-input)] text-[var(--text-secondary)]'}`}
            >
              <span>{t('replay.musicMode.off', 'Av')}</span>
              {musicMode === 'off' && <CheckCircle2 size={15} className="text-brand" />}
            </button>

            <button
              type="button"
              onClick={() => { stopMusicPreview(); setMusicSheetOpen(false); }}
              className="w-full bg-brand hover:bg-brand-dark text-stone-900 py-3 rounded-xl text-tiny font-black uppercase tracking-widest transition-colors"
            >
              {t('replay.compose.done', 'Ferdig')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReplayOverlay;
